<template>
  <BaseTemplate app-name="Eventos Adversos" page-title="">
    <template #content>
      <div class="w-full px-4 md:px-8 py-4 sm:py-8">
        <!-- Banner Message (fora do modal: canto inferior direito da tela) -->
        <Transition enter-active-class="transition-all duration-300 ease-out" leave-active-class="transition-all duration-300 ease-in" enter-from-class="-translate-y-5 opacity-0" leave-to-class="-translate-y-5 opacity-0">
          <div v-if="showBanner && !showModal" :class="[
            'fixed bottom-4 right-4 z-[60] max-w-md w-full rounded-xl shadow-2xl p-4 flex items-center justify-between',
            bannerType === 'success'
              ? 'bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800'
              : 'bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800'
          ]">
            <div class="flex items-center flex-1 min-w-0">
              <div :class="[
                'flex-shrink-0 mr-3',
                bannerType === 'success' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
              ]">
                <svg v-if="bannerType === 'success'" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <p :class="[
                'text-sm font-medium flex-1 truncate',
                bannerType === 'success'
                  ? 'text-green-800 dark:text-green-200'
                  : 'text-red-800 dark:text-red-200'
              ]">
                {{ bannerMessage }}
              </p>
            </div>
            <button @click="showBanner = false" :class="[
              'ml-3 flex-shrink-0',
              bannerType === 'success'
                ? 'text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200'
                : 'text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200'
            ]">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </Transition>

        <div class="w-full">
          <!-- Header -->
          <div class="mb-6 flex justify-between items-center">
            <h1 class="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">Eventos Adversos</h1>
            <button v-if="canVerGraficosRelatorios" @click="toggleGraficosRelatorios"
              class="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all font-medium flex items-center gap-2">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z">
                </path>
              </svg>
              {{ showGraficosRelatorios ? 'Ocultar' : 'Gráficos e Relatórios' }}
            </button>
          </div>
          <div v-if="!canListarEventos && !showGraficosRelatorios"
            class="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-4 sm:p-6 mb-6 text-gray-600 dark:text-gray-300">
            Você não tem permissão para listar eventos adversos.
          </div>

          <!-- Seção de Gráficos e Relatórios -->
          <Transition enter-active-class="transition-all duration-300 ease-out" leave-active-class="transition-all duration-300 ease-in" enter-from-class="-translate-y-5 opacity-0" leave-to-class="translate-y-5 opacity-0">
            <div v-if="showGraficosRelatorios" class="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-4 sm:p-6 mb-6">
              <!-- Abas de Gráficos e Relatórios -->
              <div class="flex space-x-1 border-b border-gray-200 dark:border-gray-700 mb-6">
                <button v-if="canVerGraficos" @click="graficosRelatoriosTab = 'graficos'" :class="[
                  'px-4 py-2 text-sm font-medium transition-colors',
                  graficosRelatoriosTab === 'graficos'
                    ? 'border-b-2 border-blue-600 text-blue-600 dark:text-blue-400'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                ]">
                  Gráficos
                </button>
                <button v-if="canVerRelatorios" @click="graficosRelatoriosTab = 'relatorios'" :class="[
                  'px-4 py-2 text-sm font-medium transition-colors',
                  graficosRelatoriosTab === 'relatorios'
                    ? 'border-b-2 border-blue-600 text-blue-600 dark:text-blue-400'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                ]">
                  Relatórios
                </button>
              </div>

              <!-- Conteúdo Gráficos -->
              <div v-if="graficosRelatoriosTab === 'graficos' && canVerGraficos" class="space-y-6">
                <div v-if="carregandoGraficos" class="text-center py-8">
                  <svg class="animate-spin h-8 w-8 text-blue-600 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none"
                    viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                    </path>
                  </svg>
                  <p class="mt-4 text-gray-600 dark:text-gray-400">Carregando gráficos...</p>
                </div>

                <div v-else class="space-y-6">
                  <!-- Métricas Resumidas -->
                  <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div
                      class="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                      <p class="text-sm text-blue-600 dark:text-blue-400 font-medium">Tempo Médio de Resolução</p>
                      <p class="text-2xl font-bold text-blue-800 dark:text-blue-200">{{
                        dadosGraficos.tempo_medio_resolucao || 0 }} dias</p>
                    </div>
                    <div
                      class="bg-green-50 dark:bg-green-900/30 rounded-lg p-4 border border-green-200 dark:border-green-800">
                      <p class="text-sm text-green-600 dark:text-green-400 font-medium">Total de Eventos</p>
                      <p class="text-2xl font-bold text-green-800 dark:text-green-200">{{ dadosGraficos.total_eventos ||
                        0 }}</p>
                    </div>
                    <div
                      class="bg-purple-50 dark:bg-purple-900/30 rounded-lg p-4 border border-purple-200 dark:border-purple-800">
                      <p class="text-sm text-purple-600 dark:text-purple-400 font-medium">Eventos Concluídos</p>
                      <p class="text-2xl font-bold text-purple-800 dark:text-purple-200">{{
                        dadosGraficos.eventos_concluidos || 0 }}</p>
                    </div>
                  </div>

                  <!-- Gráficos -->
                  <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <!-- Gráfico por Status -->
                    <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                      <h3 class="text-lg font-semibold text-gray-800 dark:text-white mb-4">Eventos por Status</h3>
                      <div class="h-64">
                        <canvas ref="chartStatus"></canvas>
                      </div>
                    </div>

                    <!-- Gráfico por Mês -->
                    <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                      <h3 class="text-lg font-semibold text-gray-800 dark:text-white mb-4">Eventos por Mês (Últimos 12
                        meses)</h3>
                      <div class="h-64">
                        <canvas ref="chartMeses"></canvas>
                      </div>
                    </div>

                    <!-- Gráfico por Grau de Dano -->
                    <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 lg:col-span-2">
                      <h3 class="text-lg font-semibold text-gray-800 dark:text-white mb-4">Eventos por Grau de Dano</h3>
                      <div class="h-64">
                        <canvas ref="chartGrau"></canvas>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Conteúdo Relatórios -->
              <div v-if="graficosRelatoriosTab === 'relatorios' && canVerRelatorios" class="space-y-6">
                <!-- Filtros do Relatório -->
                <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <h3 class="text-lg font-semibold text-gray-800 dark:text-white mb-4">Filtros do Relatório</h3>
                  <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Status</label>
                      <select v-model="filtrosRelatorio.status"
                        class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg">
                        <option value="">Todos</option>
                        <option value="Pendente">Pendente</option>
                        <option value="Em Análise">Em Análise</option>
                        <option value="FASE 3">FASE 3</option>
                        <option value="Em Follow-up">Em Follow-up</option>
                        <option value="FASE 4">FASE 4</option>
                        <option value="Em Avaliação">Em Avaliação</option>
                        <option value="Concluído">Concluído</option>
                      </select>
                    </div>

                    <div>
                      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Data Início</label>
                      <input v-model="filtrosRelatorio.data_inicio" type="date"
                        class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg">
                    </div>

                    <div>
                      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Data Fim</label>
                      <input v-model="filtrosRelatorio.data_fim" type="date"
                        class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg">
                    </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Local do
                        Evento</label>
                      <select v-model="filtrosRelatorio.local_evento"
                        class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg">
                        <option value="">Todos</option>
                        <option v-for="local in locaisEventoOptions" :key="local" :value="local">
                          {{ local }}
                        </option>
                      </select>
                    </div>

                    <div>
                      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Grau de
                        Dano</label>
                      <select v-model="filtrosRelatorio.grau_dano"
                        class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg">
                        <option value="">Todos</option>
                        <option value="1">Leve</option>
                        <option value="2">Moderado</option>
                        <option value="3">Grave</option>
                      </select>
                    </div>

                    <div>
                      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Tipo do Evento
                        Adverso</label>
                      <select v-model="filtrosRelatorio.tipo_evento"
                        class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg">
                        <option value="">Todos</option>
                        <option v-for="tipo in tiposEventoOptions" :key="tipo" :value="tipo">
                          {{ tipo }}
                        </option>
                      </select>
                    </div>

                    <div>
                      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Resolvido no
                        Prazo</label>
                      <select v-model="filtrosRelatorio.resolvido_prazo"
                        class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg">
                        <option :value="null">Todos</option>
                        <option :value="true">Sim</option>
                        <option :value="false">Não</option>
                      </select>
                    </div>
                  </div>

                  <div class="mt-4 flex gap-3">
                    <button @click="carregarRelatorio"
                      class="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all font-medium">
                      Gerar Relatório
                    </button>
                    <button v-if="dadosRelatorio.eventos && dadosRelatorio.eventos.length > 0" @click="gerarPDF"
                      class="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all font-medium flex items-center gap-2">
                      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z">
                        </path>
                      </svg>
                      Exportar PDF
                    </button>
                  </div>
                </div>

                <!-- Resultados do Relatório -->
                <div v-if="dadosRelatorio.eventos && dadosRelatorio.eventos.length > 0"
                  class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <h3 class="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                    Resultados: {{ dadosRelatorio.total }} evento(s) encontrado(s)
                  </h3>
                  <div class="overflow-x-auto">
                    <table class="w-full text-sm">
                      <thead class="bg-gray-200 dark:bg-gray-600">
                        <tr>
                          <th class="px-4 py-2 text-left">Protocolo</th>
                          <th class="px-4 py-2 text-left">Paciente</th>
                          <th class="px-4 py-2 text-left">Tipo do Evento</th>
                          <th class="px-4 py-2 text-left">Data Evento</th>
                          <th class="px-4 py-2 text-left">Status</th>
                          <th class="px-4 py-2 text-left">Local</th>
                          <th class="px-4 py-2 text-left">Grau Dano</th>
                          <th class="px-4 py-2 text-left">Dias Resolução</th>
                          <th class="px-4 py-2 text-left">Dentro Prazo</th>
                        </tr>
                      </thead>
                      <tbody class="divide-y divide-gray-200 dark:divide-gray-600">
                        <tr v-for="evento in dadosRelatorio.eventos" :key="evento.protocolo"
                          class="hover:bg-gray-100 dark:hover:bg-gray-600">
                          <td class="px-4 py-2">{{ evento.protocolo }}</td>
                          <td class="px-4 py-2">{{ evento.paciente }}</td>
                          <td class="px-4 py-2">{{ Array.isArray(evento.tipos_evento) ? evento.tipos_evento.join(', ') : (evento.tipos_evento || '-') }}</td>
                          <td class="px-4 py-2">{{ formatDate(evento.data_evento) }}</td>
                          <td class="px-4 py-2">
                            <span :class="getStatusClass(evento.status)"
                              class="px-2 py-1 text-xs font-medium rounded-full">
                              {{ evento.status }}
                            </span>
                          </td>
                          <td class="px-4 py-2">{{ evento.local_evento }}</td>
                          <td class="px-4 py-2">{{ evento.grau_dano ? (evento.grau_dano === '1' ? 'Leve' :
                            evento.grau_dano === '2' ? 'Moderado' : 'Grave') : '-' }}</td>
                          <td class="px-4 py-2">{{ evento.dias_resolucao !== null ? evento.dias_resolucao + ' dias' :
                            '-' }}</td>
                          <td class="px-4 py-2">
                            <span v-if="evento.dentro_prazo !== null"
                              :class="evento.dentro_prazo ? 'text-green-600' : 'text-red-600'">
                              {{ evento.dentro_prazo ? 'Sim' : 'Não' }}
                            </span>
                            <span v-else>-</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div v-else-if="!carregandoRelatorio && dadosRelatorio.eventos && dadosRelatorio.eventos.length === 0"
                  class="text-center py-8 text-gray-500 dark:text-gray-400">
                  Nenhum evento encontrado com os filtros aplicados.
                </div>
              </div>
            </div>
          </Transition>

          <!-- Filtros e Busca -->
          <Transition enter-active-class="transition-all duration-300 ease-out" leave-active-class="transition-all duration-300 ease-in" enter-from-class="-translate-y-5 opacity-0" leave-to-class="translate-y-5 opacity-0">
            <div v-if="!showGraficosRelatorios && canListarEventos"
              class="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-4 sm:p-6 mb-6">
              <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div class="md:col-span-2">
                  <input v-model="searchTerm" type="text" placeholder="Buscar por protocolo, paciente ou responsável..."
                    class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all">
                </div>
                <div>
                  <select v-model="selectedStatus"
                    class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all">
                    <option value="todos">Todos os Status</option>
                    <option value="Pendente">Pendente</option>
                    <option value="Em Análise">Em Análise</option>
                    <option value="FASE 3">FASE 3</option>
                    <option value="Em Follow-up">Em Follow-up</option>
                    <option value="FASE 4">FASE 4</option>
                    <option value="Em Avaliação">Em Avaliação</option>
                    <option value="Concluído">Concluído</option>
                    <option value="Arquivado">Arquivado</option>
                  </select>
                </div>
                <button @click="loadEventos"
                  class="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all font-medium flex items-center justify-center">
                  <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15">
                    </path>
                  </svg>
                  Atualizar
                </button>
              </div>
              <div class="mt-4 flex flex-col sm:flex-row items-center justify-between">
                <div class="flex items-center space-x-2">
                  <label class="text-sm font-medium text-gray-700 dark:text-gray-300">Itens por página:</label>
                  <select v-model="itemsPerPage" @change="loadEventos"
                    class="px-3 py-1 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all">
                    <option value="10">10</option>
                    <option value="15">15</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                  </select>
                </div>
              </div>
            </div>
          </Transition>

          <!-- Lista de Eventos -->
          <Transition enter-active-class="transition-all duration-300 ease-out" leave-active-class="transition-all duration-300 ease-in" enter-from-class="-translate-y-5 opacity-0" leave-to-class="translate-y-5 opacity-0">
            <div v-if="!showGraficosRelatorios && canListarEventos"
              class="hidden md:block bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden">
              <div class="overflow-x-auto">
                <table class="w-full">
                  <thead class="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th
                        class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Protocolo</th>
                      <th
                        class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Paciente</th>
                      <th
                        class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Data do Evento</th>
                      <th
                        class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Data de Criação</th>
                      <th
                        class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Status</th>
                      <th
                        class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Ações</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-200 dark:divide-gray-600">
                    <tr v-for="evento in filteredEventos" :key="evento.id"
                      class="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                      <td class="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                        {{ evento.protocolo }}
                      </td>
                      <td class="px-4 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                        {{ evento.paciente }}
                      </td>
                      <td class="px-4 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                        {{ formatDate(evento.data_ocorrencia) }}
                      </td>
                      <td class="px-4 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                        {{ formatDate(evento.data_registro) }}
                      </td>
                      <td class="px-4 py-4 whitespace-nowrap">
                        <span :class="getStatusClass(evento.status)" class="px-2 py-1 text-xs font-medium rounded-full">
                          {{ evento.status || 'Pendente' }}
                        </span>
                      </td>
                      <td class="px-4 py-4 whitespace-nowrap text-sm">
                        <div class="flex items-center space-x-2">
                          <button v-if="canGerenciarEventos" @click="openModal(evento)"
                            class="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg transition-all text-xs font-medium">
                            Gerenciar
                          </button>
                          <button v-if="canVerEvento" @click="openDetailsModal(evento)"
                            class="bg-gray-600 hover:bg-gray-700 text-white px-2 py-1.5 rounded-lg transition-all"
                            title="Ver detalhes">
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z">
                              </path>
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </Transition>

          <!-- Lista de Eventos (Mobile) -->
          <Transition enter-active-class="transition-all duration-300 ease-out" leave-active-class="transition-all duration-300 ease-in" enter-from-class="-translate-y-5 opacity-0" leave-to-class="translate-y-5 opacity-0">
            <div v-if="!showGraficosRelatorios && canListarEventos" class="block md:hidden space-y-4 -mx-3" key="mobile-events">
              <div v-for="evento in filteredEventos" :key="evento.id"
                class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-md p-4 sm:p-6 mx-3 hover:shadow-lg transition-all">
                <!-- Protocolo - Highlighted -->
                <div class="mb-3 pb-3 border-b border-gray-200 dark:border-gray-700">
                  <div class="text-xs text-gray-500 dark:text-gray-400 mb-1">Protocolo</div>
                  <div class="text-base font-bold text-gray-900 dark:text-white">
                    {{ evento.protocolo }}
                  </div>
                </div>

                <!-- Paciente -->
                <div class="mb-3">
                  <div class="text-xs text-gray-500 dark:text-gray-400 mb-1">Paciente</div>
                  <div class="text-sm text-gray-900 dark:text-white">
                    {{ evento.paciente }}
                  </div>
                </div>

                <!-- Data do Evento -->
                <div class="mb-3">
                  <div class="text-xs text-gray-500 dark:text-gray-400 mb-1">Data do Evento</div>
                  <div class="text-sm text-gray-900 dark:text-white">
                    {{ formatDate(evento.data_ocorrencia) }}
                  </div>
                </div>

                <!-- Data de Criação -->
                <div class="mb-4">
                  <div class="text-xs text-gray-500 dark:text-gray-400 mb-1">Data de Criação</div>
                  <div class="text-sm text-gray-900 dark:text-white">
                    {{ formatDate(evento.data_registro) }}
                  </div>
                </div>

                <!-- Status and Actions Row -->
                <div class="flex items-center justify-between pt-3 border-t border-gray-200 dark:border-gray-700">
                  <!-- Status Badge -->
                  <div>
                    <span :class="getStatusClass(evento.status)" class="px-2.5 py-1 text-xs font-medium rounded-full">
                      {{ evento.status || 'Pendente' }}
                    </span>
                  </div>

                  <!-- Action Buttons -->
                  <div class="flex items-center gap-2">
                    <button v-if="canGerenciarEventos" @click="openModal(evento)"
                      class="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg transition-all text-xs font-medium">
                      Gerenciar
                    </button>
                    <button v-if="canVerEvento" @click="openDetailsModal(evento)"
                      class="bg-gray-600 hover:bg-gray-700 text-white px-2 py-1.5 rounded-lg transition-all"
                      title="Ver detalhes">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z">
                        </path>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </Transition>

          <!-- Empty State for Mobile -->
          <Transition enter-active-class="transition-all duration-300 ease-out" leave-active-class="transition-all duration-300 ease-in" enter-from-class="-translate-y-5 opacity-0" leave-to-class="translate-y-5 opacity-0">
            <div v-if="!showGraficosRelatorios && canListarEventos && filteredEventos.length === 0"
              class="text-center py-8 text-gray-500 dark:text-gray-400">
              Nenhum evento encontrado
            </div>
          </Transition>
        </div>

        <!-- Paginação -->
          <Transition enter-active-class="transition-all duration-300 ease-out" leave-active-class="transition-all duration-300 ease-in" enter-from-class="-translate-y-5 opacity-0" leave-to-class="translate-y-5 opacity-0">
          <div v-if="!showGraficosRelatorios && canListarEventos"
            class="mt-6 flex flex-col sm:flex-row items-center justify-between bg-white dark:bg-gray-800 rounded-2xl p-4 sm:p-6 mb-4 sm:mb-0">
            <div class="text-sm text-gray-700 dark:text-gray-300 mb-2 sm:mb-0">
              Mostrando {{ ((currentPage - 1) * itemsPerPage) + 1 }} - {{ Math.min(currentPage * itemsPerPage,
              totalItems) }} de {{ totalItems }} itens
            </div>
            <div class="flex space-x-2">
              <button @click="previousPage" :disabled="currentPage === 1"
                class="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all">
                Anterior
              </button>
              <span class="flex items-center px-3 py-1">
                Página {{ currentPage }} de {{ totalPages }}
              </span>
              <button @click="nextPage" :disabled="currentPage === totalPages"
                class="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all">
                Próxima
              </button>
            </div>
          </div>
        </Transition>


        <!-- Modal de Edição -->
        <div v-if="showModal"
          class="fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4 sm:p-6 pt-14 sm:pt-16">
          <div class="relative flex flex-col bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] mt-1 sm:mt-2">
            <!-- Área rolável do modal (header + conteúdo + rodapé) -->
            <div class="flex-1 overflow-y-auto min-h-0">
            <!-- Header do Modal -->
            <div
              class="sticky top-0 z-[1] bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4 sm:p-6 rounded-t-2xl">
              <div class="flex justify-between items-center">
                <div>
                  <h2 class="text-xl font-bold text-gray-800 dark:text-white">Gerenciar Evento Adverso</h2>
                  <p class="text-sm text-gray-600 dark:text-gray-300">Protocolo: {{ selectedEvento?.protocolo }}</p>
                </div>
                <button @click="closeModal"
                  class="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                  <svg class="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12">
                    </path>
                  </svg>
                </button>
              </div>

            <div class="mt-4 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
              <div class="flex overflow-x-auto space-x-1">
                <button v-for="fase in fases" :key="fase.id"
                  @click="currentPhase = fase.id; showAllAnexos = false; showDemandaJudicial = false"
                  :disabled="!isFaseDisponivel(fase.id)" :class="[
                      'flex-shrink-0 sm:flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all whitespace-nowrap',
                      !showAllAnexos && currentPhase === fase.id
                        ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-800 dark:hover:text-white',
                      !isFaseDisponivel(fase.id)
                        ? 'opacity-50 cursor-not-allowed'
                        : ''
                    ]">
                  {{ fase.name }}
                </button>
                <button v-if="canVerAnexos || canAnexar"
                  @click="showAllAnexos = true; showDemandaJudicial = false"
                  :class="[
                    'flex-shrink-0 sm:flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all whitespace-nowrap',
                    showAllAnexos
                      ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-800 dark:hover:text-white'
                  ]">
                  Documentos anexados
                </button>
                <button v-if="canVerAnexos || canAnexar"
                  @click="showDemandaJudicial = true; showAllAnexos = false"
                  :class="[
                    'flex-shrink-0 sm:flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all whitespace-nowrap',
                    showDemandaJudicial
                      ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-800 dark:hover:text-white'
                  ]">
                  Demanda Judicial
                </button>
              </div>
            </div>
            </div>
            <div class="p-4 sm:p-6">
              <div v-if="!showAllAnexos && !showDemandaJudicial && currentPhase === 1" class="space-y-6">
                <h3 class="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center">
                  <span
                    class="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">1</span>
                  Informações do Evento
                </h3>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Protocolo
                    </label>
                    <input :value="selectedEvento?.protocolo" type="text" disabled
                      class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg">
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Paciente
                    </label>
                    <input :value="selectedEvento?.paciente" type="text" disabled
                      class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg">
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Quem Abriu
                    </label>
                    <input :value="selectedEvento?.responsavel" type="text" disabled
                      class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg">
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Nome do Responsável
                    </label>
                    <input :value="selectedEvento?.responsavel" type="text" disabled
                      class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg">
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      CPF do Usuário
                    </label>
                    <input :value="selectedEvento?.cpf_usuario" type="text" disabled
                      class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg">
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Data de Nascimento
                    </label>
                    <input :value="formatDate(selectedEvento?.data_nascimento)" type="text" disabled
                      class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg">
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Data do Evento
                    </label>
                    <input :value="formatDate(selectedEvento?.data_ocorrencia)" type="text" disabled
                      class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg">
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Data de Criação
                    </label>
                    <input :value="formatDate(selectedEvento?.data_registro)" type="text" disabled
                      class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg">
                  </div>
                </div>

                <div class="space-y-6">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Tipos de Evento
                    </label>
                    <textarea
                      :value="Array.isArray(selectedEvento?.tipos_evento) ? selectedEvento?.tipos_evento.join(', ') : selectedEvento?.tipos_evento"
                      disabled
                      class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg resize-none"
                      rows="2"></textarea>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Local do Evento
                    </label>
                    <input :value="selectedEvento?.local_evento" type="text" disabled
                      class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg">
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Condições do Usuário
                    </label>
                    <textarea :value="selectedEvento?.condicoes_usuario" disabled
                      class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg resize-none"
                      rows="3"></textarea>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Resumo do Evento
                    </label>
                    <textarea :value="selectedEvento?.resumo_evento" disabled
                      class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg resize-none"
                      rows="4"></textarea>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Ação de Correção Imediata

                    </label>
                    <textarea :value="selectedEvento?.acao_correcao" disabled
                      class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg resize-none"
                      rows="3"></textarea>
                  </div>
                </div>
              </div>

              <div v-if="!showAllAnexos && !showDemandaJudicial && currentPhase === 2" class="space-y-6">
                <h3 class="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center">
                  <span
                    class="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">2</span>
                  Análise NSP
                </h3>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Data do Início da Análise
                    </label>
                    <input v-model="modalForm.dataInicioAnalise" type="date" :disabled="isFaseReadOnly(2)"
                      class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:bg-gray-100 disabled:cursor-not-allowed">
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Data da Devolução da Análise
                    </label>
                    <input v-model="modalForm.dataDevolucaoAnalise" type="date" :disabled="isFaseReadOnly(2)"
                      class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:bg-gray-100 disabled:cursor-not-allowed">
                  </div>

                  <div class="md:col-span-2">
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Membro do NSP/Gestor Responsável pela Análise
                    </label>
                    <select v-model="modalForm.responsavelAnalise" :disabled="isFaseReadOnly(2)"
                      class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:bg-gray-100 disabled:cursor-not-allowed">
                      <option value="">Selecione o responsável...</option>
                      <option v-for="user in usuariosFase2" :key="user.id" :value="user.name">
                        {{ user.name }}
                      </option>
                    </select>
                  </div>

                  <div class="md:col-span-2">
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Responsável pela Ação
                    </label>
                    <select v-model="modalForm.responsavelAcao" :disabled="isFaseReadOnly(2)"
                      class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:bg-gray-100 disabled:cursor-not-allowed">
                      <option value="">Selecione o responsável...</option>
                      <option v-for="user in usuariosFase2" :key="user.id" :value="user.name">
                        {{ user.name }}
                      </option>
                    </select>
                  </div>
                </div>

                <div class="mt-6 border-t border-gray-200 dark:border-gray-700 pt-4">
                  <div class="flex items-center justify-between mb-3">
                    <h4 class="text-sm font-semibold text-gray-800 dark:text-gray-100">
                      Anexos da Fase 2
                    </h4>
                    <div class="flex items-center gap-3" v-if="canAnexar || canVerAnexos">
                      <label
                        class="inline-flex items-center px-3 py-1.5 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-xs sm:text-sm text-gray-700 dark:text-gray-200 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition"
                        :class="{ 'opacity-60 cursor-not-allowed': !canAnexar }">
                        <input type="file" multiple class="hidden" :disabled="!canAnexar"
                          @change="onArquivoAnexoChange($event, '2')" />
                        <span>Selecionar arquivo</span>
                      </label>
                      <button type="button" @click="uploadAnexoFase('2')"
                        :disabled="!canAnexar || !arquivosUpload['2'] || isUploadingAnexo"
                        class="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs sm:text-sm hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition">
                        {{ isUploadingAnexo ? 'Enviando...' : 'Anexar' }}
                      </button>
                      <span v-if="arquivosUpload['2'] && arquivosUpload['2'].length"
                        class="text-xs text-gray-500 dark:text-gray-300 max-w-[220px] truncate"
                        :title="arquivosUpload['2'].map(f => f.name).join(', ')">
                        {{ arquivosUpload['2'].length === 1
                          ? arquivosUpload['2'][0].name
                          : arquivosUpload['2'][0].name + ' +' + (arquivosUpload['2'].length - 1) + ' arquivos' }}
                      </span>
                    </div>
                  </div>
                  <div v-if="!canVerAnexos" class="text-xs text-gray-500 dark:text-gray-400 italic">
                    Sem permissão para visualizar anexos desta fase.
                  </div>
                  <div v-else-if="getAnexosByFase('2').length === 0"
                    class="text-xs text-gray-500 dark:text-gray-400 italic">
                    Nenhum anexo cadastrado para esta fase.
                  </div>
                  <ul v-else class="space-y-1 max-h-40 overflow-y-auto text-xs sm:text-sm">
                    <li v-for="anexo in getAnexosByFase('2')" :key="anexo.id"
                      class="flex items-center justify-between gap-2">
                      <button type="button" class="text-blue-600 dark:text-blue-300 hover:underline text-left flex-1"
                        @click="abrirAnexo(anexo)">
                        {{ anexo.nome }}
                      </button>
                      <span class="text-[11px] text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {{ anexo.data_upload ? anexo.data_upload.split('T')[0] : '' }}
                      </span>
                    </li>
                  </ul>
                </div>
              </div>

              <div v-if="!showAllAnexos && !showDemandaJudicial && currentPhase === 21" class="space-y-6">
                <h3 class="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center">
                  <span
                    class="w-8 h-8 bg-orange-400 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">2.1</span>
                  Análise Gestor
                </h3>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div class="md:col-span-2">
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      3 - Identificação da Causa
                    </label>
                    <textarea v-model="modalForm.identificacaoCausa" rows="4"
                      :disabled="isFaseReadOnly(21) || camposBloqueadosFase21.identificacaoCausa"
                      class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder="Descreva a causa identificada..."></textarea>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      4 - Possui Classificação de Dano?
                    </label>
                    <div class="flex gap-4">
                      <button type="button" class="flex-1 px-4 py-3 border rounded-lg font-semibold transition-all"
                        :class="[
                          modalForm.classificacaoDano === 'Sim'
                            ? 'bg-blue-600 text-white border-blue-600 shadow'
                            : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600',
                          (isFaseReadOnly(21) || camposBloqueadosFase21.classificacaoDano)
                            ? 'opacity-60 cursor-not-allowed hover:bg-white dark:hover:bg-gray-700'
                            : ''
                        ]"
                        :disabled="isFaseReadOnly(21) || camposBloqueadosFase21.classificacaoDano"
                        @click="setClassificacaoDano('Sim')"
                        aria-pressed="modalForm.classificacaoDano === 'Sim'">
                        Sim
                      </button>
                      <button type="button" class="flex-1 px-4 py-3 border rounded-lg font-semibold transition-all"
                        :class="[
                          modalForm.classificacaoDano === 'Não'
                            ? 'bg-red-600 text-white border-red-600 shadow'
                            : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600',
                          (isFaseReadOnly(21) || camposBloqueadosFase21.classificacaoDano)
                            ? 'opacity-60 cursor-not-allowed hover:bg-white dark:hover:bg-gray-700'
                            : ''
                        ]"
                        :disabled="isFaseReadOnly(21) || camposBloqueadosFase21.classificacaoDano"
                        @click="setClassificacaoDano('Não')"
                        aria-pressed="modalForm.classificacaoDano === 'Não'">
                        Não
                      </button>
                    </div>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      5 - Grau do Dano
                    </label>
                    <select v-model="modalForm.grauDano" :disabled="isGrauDanoDisabled"
                      class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:bg-gray-100 disabled:cursor-not-allowed">
                      <option value="">{{ modalForm.classificacaoDano === 'Sim' ? 'Selecione...' : 'Selecione Sim para habilitar' }}</option>
                      <option value="1">Leve</option>
                      <option value="2">Moderado</option>
                      <option value="3">Grave</option>
                      <option value="3">Óbito</option>
                    </select>
                  </div>

                  <div class="md:col-span-2">
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      6 - Ação Corretiva
                    </label>
                    <textarea v-model="modalForm.acaoCorretiva" rows="4"
                      :disabled="isFaseReadOnly(21) || camposBloqueadosFase21.acaoCorretiva"
                      class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder="Descreva a ação corretiva..."></textarea>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Prazo
                    </label>
                    <input v-model="modalForm.prazoAcao" type="date"
                      :disabled="isFaseReadOnly(21) || camposBloqueadosFase21.prazoAcao"
                      class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:bg-gray-100 disabled:cursor-not-allowed">
                  </div>

                  <div class="md:col-span-2">
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Evidência da Conclusão
                    </label>
                    <textarea v-model="modalForm.evidenciaConclusao" rows="3"
                      :disabled="isFaseReadOnly(21) || camposBloqueadosFase21.evidenciaConclusao"
                      class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder="Descreva as evidências..."></textarea>
                  </div>
                </div>

                <div class="mt-6 border-t border-gray-200 dark:border-gray-700 pt-4">
                  <div class="flex items-center justify-between mb-3">
                    <h4 class="text-sm font-semibold text-gray-800 dark:text-gray-100">
                      Anexos da Fase 2.1
                    </h4>
                    <div class="flex items-center gap-3" v-if="canAnexar || canVerAnexos">
                      <label
                        class="inline-flex items-center px-3 py-1.5 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-xs sm:text-sm text-gray-700 dark:text-gray-200 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition"
                        :class="{ 'opacity-60 cursor-not-allowed': !canAnexar }">
                        <input type="file" multiple class="hidden" :disabled="!canAnexar"
                          @change="onArquivoAnexoChange($event, '2.1')" />
                        <span>Selecionar arquivo</span>
                      </label>
                      <button type="button" @click="uploadAnexoFase('2.1')"
                        :disabled="!canAnexar || !arquivosUpload['2.1'] || isUploadingAnexo"
                        class="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs sm:text-sm hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition">
                        {{ isUploadingAnexo ? 'Enviando...' : 'Anexar' }}
                      </button>
                      <span v-if="arquivosUpload['2.1'] && arquivosUpload['2.1'].length"
                        class="text-xs text-gray-500 dark:text-gray-300 max-w-[220px] truncate"
                        :title="arquivosUpload['2.1'].map(f => f.name).join(', ')">
                        {{ arquivosUpload['2.1'].length === 1
                          ? arquivosUpload['2.1'][0].name
                          : arquivosUpload['2.1'][0].name + ' +' + (arquivosUpload['2.1'].length - 1) + ' arquivos' }}
                      </span>
                    </div>
                  </div>
                  <div v-if="!canVerAnexos" class="text-xs text-gray-500 dark:text-gray-400 italic">
                    Sem permissão para visualizar anexos desta fase.
                  </div>
                  <div v-else-if="getAnexosByFase('2.1').length === 0"
                    class="text-xs text-gray-500 dark:text-gray-400 italic">
                    Nenhum anexo cadastrado para esta fase.
                  </div>
                  <ul v-else class="space-y-1 max-h-40 overflow-y-auto text-xs sm:text-sm">
                    <li v-for="anexo in getAnexosByFase('2.1')" :key="anexo.id"
                      class="flex items-center justify-between gap-2">
                      <button type="button" class="text-blue-600 dark:text-blue-300 hover:underline text-left flex-1"
                        @click="abrirAnexo(anexo)">
                        {{ anexo.nome }}
                      </button>
                      <span class="text-[11px] text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {{ anexo.data_upload ? anexo.data_upload.split('T')[0] : '' }}
                      </span>
                    </li>
                  </ul>
                </div>
              </div>

              <div v-if="!showAllAnexos && !showDemandaJudicial && currentPhase === 3" class="space-y-6">
                <h3 class="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center">
                  <span
                    class="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">3</span>
                  Follow-up
                </h3>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Data para Realizar Follow-up
                    </label>
                    <input v-model="modalForm.dataFollowUp" type="date" :disabled="isFaseReadOnly(3)"
                      class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:bg-gray-100 disabled:cursor-not-allowed">
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Data da Devolução do Follow-up
                    </label>
                    <input v-model="modalForm.dataDevolucaoFollowUp" type="date" :disabled="isFaseReadOnly(3)"
                      class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:bg-gray-100 disabled:cursor-not-allowed">
                  </div>

                  <div class="md:col-span-2">
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      7 - Análise do Follow-up
                    </label>
                    <textarea v-model="modalForm.analiseFollowUp" rows="4" :disabled="isFaseReadOnly(3)"
                      class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder="Descreva a análise do follow-up..."></textarea>
                  </div>

                  <div class="md:col-span-2">
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Responsável pelo Follow-up
                    </label>
                    <select v-model="modalForm.responsavelFollowUp" :disabled="isFaseReadOnly(3)"
                      class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:bg-gray-100 disabled:cursor-not-allowed">
                      <option value="">Selecione o responsável...</option>
                      <option v-for="user in usuariosFase3" :key="user.id" :value="user.name">
                        {{ user.name }}
                      </option>
                    </select>
                  </div>
                </div>

                <div class="mt-6 border-t border-gray-200 dark:border-gray-700 pt-4">
                  <div class="flex items-center justify-between mb-3">
                    <h4 class="text-sm font-semibold text-gray-800 dark:text-gray-100">
                      Anexos da Fase 3
                    </h4>
                    <div class="flex items-center gap-3" v-if="canAnexar || canVerAnexos">
                      <label
                        class="inline-flex items-center px-3 py-1.5 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-xs sm:text-sm text-gray-700 dark:text-gray-200 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition"
                        :class="{ 'opacity-60 cursor-not-allowed': !canAnexar }">
                        <input type="file" multiple class="hidden" :disabled="!canAnexar"
                          @change="onArquivoAnexoChange($event, '3')" />
                        <span>Selecionar arquivo</span>
                      </label>
                      <button type="button" @click="uploadAnexoFase('3')"
                        :disabled="!canAnexar || !arquivosUpload['3'] || isUploadingAnexo"
                        class="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs sm:text-sm hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition">
                        {{ isUploadingAnexo ? 'Enviando...' : 'Anexar' }}
                      </button>
                      <span v-if="arquivosUpload['3'] && arquivosUpload['3'].length"
                        class="text-xs text-gray-500 dark:text-gray-300 max-w-[220px] truncate"
                        :title="arquivosUpload['3'].map(f => f.name).join(', ')">
                        {{ arquivosUpload['3'].length === 1
                          ? arquivosUpload['3'][0].name
                          : arquivosUpload['3'][0].name + ' +' + (arquivosUpload['3'].length - 1) + ' arquivos' }}
                      </span>
                    </div>
                  </div>
                  <div v-if="!canVerAnexos" class="text-xs text-gray-500 dark:text-gray-400 italic">
                    Sem permissão para visualizar anexos desta fase.
                  </div>
                  <div v-else-if="getAnexosByFase('3').length === 0"
                    class="text-xs text-gray-500 dark:text-gray-400 italic">
                    Nenhum anexo cadastrado para esta fase.
                  </div>
                  <ul v-else class="space-y-1 max-h-40 overflow-y-auto text-xs sm:text-sm">
                    <li v-for="anexo in getAnexosByFase('3')" :key="anexo.id"
                      class="flex items-center justify-between gap-2">
                      <button type="button" class="text-blue-600 dark:text-blue-300 hover:underline text-left flex-1"
                        @click="abrirAnexo(anexo)">
                        {{ anexo.nome }}
                      </button>
                      <span class="text-[11px] text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {{ anexo.data_upload ? anexo.data_upload.split('T')[0] : '' }}
                      </span>
                    </li>
                  </ul>
                </div>
              </div>

              <div v-if="!showAllAnexos && !showDemandaJudicial && currentPhase === 4" class="space-y-6">
                <h3 class="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center">
                  <span
                    class="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">4</span>
                  Avaliação de Eficácia
                </h3>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div class="md:col-span-2">
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      8 - Avaliação de Eficácia do NSP
                    </label>
                    <textarea v-model="modalForm.avaliacaoEficacia" rows="4" :disabled="isFaseReadOnly(4)"
                      class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder="Descreva a avaliação de eficácia..."></textarea>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Responsável pela Avaliação
                    </label>
                    <select v-model="modalForm.responsavelAvaliacao" :disabled="isFaseReadOnly(4)"
                      class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:bg-gray-100 disabled:cursor-not-allowed">
                      <option value="">Selecione o responsável...</option>
                      <option v-for="user in usuariosFase4" :key="user.id" :value="user.name">
                        {{ user.name }}
                      </option>
                    </select>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Prazo da Avaliação
                    </label>
                    <input v-model="modalForm.prazoAvaliacao" type="date" :disabled="isFaseReadOnly(4)"
                      class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:bg-gray-100 disabled:cursor-not-allowed">
                  </div>

                  <div class="md:col-span-2">
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Status Final
                    </label>
                    <select v-model="modalForm.statusFinal" :disabled="isFaseReadOnly(4)"
                      class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:bg-gray-100 disabled:cursor-not-allowed">
                      <option value="">Selecione o status...</option>
                      <option value="Pendente">Pendente</option>
                      <option value="Em Análise">Em Análise</option>
                      <option value="Em Follow-up">Em Follow-up</option>
                      <option value="Concluído">Concluído</option>
                      <option value="Arquivado">Arquivado</option>
                    </select>
                  </div>
                </div>

                <div class="mt-6 border-t border-gray-200 dark:border-gray-700 pt-4">
                  <div class="flex items-center justify-between mb-3">
                    <h4 class="text-sm font-semibold text-gray-800 dark:text-gray-100">
                      Anexos da Fase 4
                    </h4>
                    <div class="flex items-center gap-3" v-if="canAnexar || canVerAnexos">
                      <label
                        class="inline-flex items-center px-3 py-1.5 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-xs sm:text-sm text-gray-700 dark:text-gray-200 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition"
                        :class="{ 'opacity-60 cursor-not-allowed': !canAnexar }">
                        <input type="file" multiple class="hidden" :disabled="!canAnexar"
                          @change="onArquivoAnexoChange($event, '4')" />
                        <span>Selecionar arquivo</span>
                      </label>
                      <button type="button" @click="uploadAnexoFase('4')"
                        :disabled="!canAnexar || !arquivosUpload['4'] || isUploadingAnexo"
                        class="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs sm:text-sm hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition">
                        {{ isUploadingAnexo ? 'Enviando...' : 'Anexar' }}
                      </button>
                      <span v-if="arquivosUpload['4'] && arquivosUpload['4'].length"
                        class="text-xs text-gray-500 dark:text-gray-300 max-w-[220px] truncate"
                        :title="arquivosUpload['4'].map(f => f.name).join(', ')">
                        {{ arquivosUpload['4'].length === 1
                          ? arquivosUpload['4'][0].name
                          : arquivosUpload['4'][0].name + ' +' + (arquivosUpload['4'].length - 1) + ' arquivos' }}
                      </span>
                    </div>
                  </div>
                  <div v-if="!canVerAnexos" class="text-xs text-gray-500 dark:text-gray-400 italic">
                    Sem permissão para visualizar anexos desta fase.
                  </div>
                  <div v-else-if="getAnexosByFase('4').length === 0"
                    class="text-xs text-gray-500 dark:text-gray-400 italic">
                    Nenhum anexo cadastrado para esta fase.
                  </div>
                  <ul v-else class="space-y-1 max-h-40 overflow-y-auto text-xs sm:text-sm">
                    <li v-for="anexo in getAnexosByFase('4')" :key="anexo.id"
                      class="flex items-center justify-between gap-2">
                      <button type="button" class="text-blue-600 dark:text-blue-300 hover:underline text-left flex-1"
                        @click="abrirAnexo(anexo)">
                        {{ anexo.nome }}
                      </button>
                      <span class="text-[11px] text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {{ anexo.data_upload ? anexo.data_upload.split('T')[0] : '' }}
                      </span>
                    </li>
                  </ul>
                </div>
              </div>

              <div v-if="showAllAnexos" class="space-y-4">
                <h3 class="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center">
                  <span
                    class="w-8 h-8 bg-gray-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">
                    D
                  </span>
                  Documentos anexados
                </h3>

                <div v-if="!canVerAnexos" class="text-sm text-gray-500 dark:text-gray-400 italic">
                  Sem permissão para visualizar anexos.
                </div>
                <div v-else-if="anexosEvento.length === 0"
                  class="text-sm text-gray-500 dark:text-gray-400 italic">
                  Nenhum documento anexado para este evento.
                </div>

                <div v-else class="space-y-2 max-h-[60vh] overflow-y-auto">
                  <div v-for="anexo in anexosEvento" :key="anexo.id"
                    class="flex items-center justify-between gap-3 p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div class="flex items-center gap-3 flex-1 min-w-0">
                      <svg class="w-5 h-5 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor"
                        viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <div class="min-w-0">
                        <p class="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">
                          {{ anexo.nome }}
                        </p>
                        <p class="text-xs text-gray-500 dark:text-gray-400" v-if="anexo.data_upload">
                          {{ anexo.data_upload.split('T')[0] }}
                        </p>
                      </div>
                    </div>
                    <button type="button"
                      class="px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-xs"
                      @click="abrirAnexo(anexo)">
                      Abrir
                    </button>
                  </div>
                </div>
              </div>

              <div v-if="showDemandaJudicial" class="space-y-4">
                <h3 class="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center">
                  <span
                    class="w-8 h-8 bg-gray-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">
                    D
                  </span>
                  Demanda judicial
                </h3>

                <div class="flex flex-wrap items-center gap-2 mb-4">
                  <input type="file" multiple accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    class="block w-full text-sm text-gray-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 dark:file:bg-blue-900/30 dark:file:text-blue-400 hover:file:bg-blue-100 dark:hover:file:bg-blue-800/50"
                    :disabled="!canAnexar"
                    @change="(e) => onArquivoAnexoChange(e, 'demanda_judicial')">
                  <button type="button" @click="uploadDemandaJudicial()"
                    :disabled="!canAnexar || !arquivosUpload['demanda_judicial'] || isUploadingAnexo"
                    class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition text-sm font-medium">
                    <span v-if="!isUploadingAnexo">Enviar arquivo(s)</span>
                    <span v-else class="flex items-center gap-2">
                      <svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                      </svg>
                      Enviando...
                    </span>
                  </button>
                  <span v-if="arquivosUpload['demanda_judicial'] && arquivosUpload['demanda_judicial'].length"
                    class="text-sm text-gray-600 dark:text-gray-400 truncate max-w-xs"
                    :title="arquivosUpload['demanda_judicial'].map(f => f.name).join(', ')">
                    {{ arquivosUpload['demanda_judicial'].length === 1
                      ? arquivosUpload['demanda_judicial'][0].name
                      : arquivosUpload['demanda_judicial'][0].name + ' +' + (arquivosUpload['demanda_judicial'].length - 1) + ' arquivos' }}
                  </span>
                </div>

                <div v-if="!canVerAnexos" class="text-sm text-gray-500 dark:text-gray-400 italic">
                  Sem permissão para visualizar anexos de demanda judicial.
                </div>
                <div v-else-if="anexosDemandaJudicial.length === 0"
                  class="text-sm text-gray-500 dark:text-gray-400 italic">
                  Nenhum documento de demanda judicial anexado para este evento.
                </div>

                <div v-else class="space-y-2 max-h-[60vh] overflow-y-auto">
                  <div v-for="anexo in anexosDemandaJudicial" :key="anexo.id"
                    class="flex items-center justify-between gap-3 p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div class="flex items-center gap-3 flex-1 min-w-0">
                      <svg class="w-5 h-5 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor"
                        viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <div class="min-w-0">
                        <p class="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">
                          {{ anexo.nome }}
                        </p>
                        <p class="text-xs text-gray-500 dark:text-gray-400" v-if="anexo.data_upload">
                          {{ anexo.data_upload.split('T')[0] }}
                        </p>
                      </div>
                    </div>
                    <button type="button"
                      class="px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-xs"
                      @click="abrirAnexo(anexo)">
                      Abrir
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div
              class="sticky bottom-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 p-4 sm:p-6 rounded-b-2xl">
              <div class="flex gap-3">
                <button @click="closeModal"
                  class="flex-1 px-4 py-2.5 sm:px-6 sm:py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all font-medium text-xs sm:text-base md:text-lg">
                  Cancelar
                </button>
                <button v-if="!showAllAnexos && !showDemandaJudicial" @click="saveChanges"
                  :disabled="isSaving || isFaseReadOnly(currentPhase)"
                  class="flex-1 px-4 py-2.5 sm:px-6 sm:py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-all font-medium text-xs sm:text-base md:text-lg">
                  <span v-if="!isSaving">Salvar Alterações</span>
                  <span v-else class="flex items-center justify-center">
                    <svg class="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg"
                      fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                      </path>
                    </svg>
                    Salvando...
                  </span>
                </button>
              </div>
            </div>
            </div>
            <!-- Banner fixo no canto inferior direito do modal (não participa da rolagem) -->
            <Transition enter-active-class="transition-all duration-300 ease-out" leave-active-class="transition-all duration-300 ease-in" enter-from-class="-translate-y-5 opacity-0" leave-to-class="-translate-y-5 opacity-0">
              <div v-if="showBanner" :class="[
                'absolute bottom-4 right-4 z-10 max-w-md rounded-xl shadow-xl p-4 flex items-center justify-between pointer-events-auto',
                bannerType === 'success'
                  ? 'bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800'
                  : 'bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800'
              ]">
                <div class="flex items-center flex-1 min-w-0">
                  <div :class="[
                    'flex-shrink-0 mr-3',
                    bannerType === 'success' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                  ]">
                    <svg v-if="bannerType === 'success'" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                  </div>
                  <p :class="[
                    'text-sm font-medium flex-1 truncate',
                    bannerType === 'success'
                      ? 'text-green-800 dark:text-green-200'
                      : 'text-red-800 dark:text-red-200'
                  ]">
                    {{ bannerMessage }}
                  </p>
                </div>
                <button @click="showBanner = false" :class="[
                  'ml-3 flex-shrink-0',
                  bannerType === 'success'
                    ? 'text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200'
                    : 'text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200'
                ]">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
              </div>
            </Transition>
          </div>
        </div>

        <!-- Modal de Detalhes -->
        <div v-if="showDetailsModal"
          class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm px-4">
          <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[80vh] overflow-y-auto">
            <!-- Header do Modal -->
            <div
              class="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4 sm:p-6 rounded-t-2xl">
              <div class="flex justify-between items-center mb-4">
                <div>
                  <h2 class="text-lg md:text-xl font-bold text-gray-800 dark:text-white">Detalhes do Evento Adverso</h2>
                  <p class="text-sm text-gray-600 dark:text-gray-300">Protocolo: {{ selectedEvento?.protocolo }}</p>
                </div>
                <button @click="closeDetailsModal"
                  class="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                  <svg class="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12">
                    </path>
                  </svg>
                </button>
              </div>

              <!-- Abas -->
              <div class="flex space-x-1 border-b border-gray-200 dark:border-gray-700">
                <button @click="detalhesTabAtiva = 'detalhes'" :class="[
                  'px-4 py-2 text-sm font-medium transition-colors',
                  detalhesTabAtiva === 'detalhes'
                    ? 'border-b-2 border-blue-600 text-blue-600 dark:text-blue-400'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                ]">
                  Detalhes
                </button>
                <button v-if="canVerHistorico" @click="detalhesTabAtiva = 'historico'; loadHistorico()" :class="[
                  'px-4 py-2 text-sm font-medium transition-colors',
                  detalhesTabAtiva === 'historico'
                    ? 'border-b-2 border-blue-600 text-blue-600 dark:text-blue-400'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                ]">
                  Histórico
                </button>
              </div>
            </div>

            <!-- Conteúdo do Modal de Detalhes -->
            <div class="p-4 sm:p-6">
              <!-- Aba Detalhes -->
              <div v-if="detalhesTabAtiva === 'detalhes'">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Protocolo
                    </label>
                    <div class="px-4 py-3 bg-gray-50 dark:bg-gray-700 rounded-lg text-gray-900 dark:text-white">
                      {{ selectedEvento?.protocolo }}
                    </div>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Paciente
                    </label>
                    <div class="px-4 py-3 bg-gray-50 dark:bg-gray-700 rounded-lg text-gray-900 dark:text-white">
                      {{ selectedEvento?.paciente }}
                    </div>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      CPF do Usuário
                    </label>
                    <div class="px-4 py-3 bg-gray-50 dark:bg-gray-700 rounded-lg text-gray-900 dark:text-white">
                      {{ selectedEvento?.cpf_usuario || '-' }}
                    </div>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Data de Nascimento
                    </label>
                    <div class="px-4 py-3 bg-gray-50 dark:bg-gray-700 rounded-lg text-gray-900 dark:text-white">
                      {{ formatDate(selectedEvento?.data_nascimento) }}
                    </div>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Data do Evento
                    </label>
                    <div class="px-4 py-3 bg-gray-50 dark:bg-gray-700 rounded-lg text-gray-900 dark:text-white">
                      {{ formatDate(selectedEvento?.data_ocorrencia) }}
                    </div>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Data de Criação
                    </label>
                    <div class="px-4 py-3 bg-gray-50 dark:bg-gray-700 rounded-lg text-gray-900 dark:text-white">
                      {{ formatDate(selectedEvento?.data_registro) }}
                    </div>
                  </div>

                  <div class="md:col-span-2">
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Tipos de Evento
                    </label>
                    <div class="px-4 py-3 bg-gray-50 dark:bg-gray-700 rounded-lg text-gray-900 dark:text-white">
                      {{ Array.isArray(selectedEvento?.tipos_evento) ? selectedEvento?.tipos_evento.join(', ') :
                        selectedEvento?.tipos_evento || '-' }}
                    </div>
                  </div>

                  <div class="md:col-span-2">
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Local do Evento
                    </label>
                    <div class="px-4 py-3 bg-gray-50 dark:bg-gray-700 rounded-lg text-gray-900 dark:text-white">
                      {{ selectedEvento?.local_evento || '-' }}
                    </div>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Status
                    </label>
                    <div class="px-4 py-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <span :class="getStatusClass(selectedEvento?.status)"
                        class="px-2 py-1 text-xs font-medium rounded-full">
                        {{ selectedEvento?.status || 'Pendente' }}
                      </span>
                    </div>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Responsável
                    </label>
                    <div class="px-4 py-3 bg-gray-50 dark:bg-gray-700 rounded-lg text-gray-900 dark:text-white">
                      {{ selectedEvento?.responsavel || '-' }}
                    </div>
                  </div>

                  <div class="md:col-span-2">
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Condições do Usuário
                    </label>
                    <div class="px-4 py-3 bg-gray-50 dark:bg-gray-700 rounded-lg text-gray-900 dark:text-white">
                      {{ selectedEvento?.condicoes_usuario || '-' }}
                    </div>
                  </div>

                  <div class="md:col-span-2">
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Resumo do Evento
                    </label>
                    <div class="px-4 py-3 bg-gray-50 dark:bg-gray-700 rounded-lg text-gray-900 dark:text-white">
                      {{ selectedEvento?.resumo_evento || '-' }}
                    </div>
                  </div>

                  <div class="md:col-span-2">
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Ação de Correção
                    </label>
                    <div class="px-4 py-3 bg-gray-50 dark:bg-gray-700 rounded-lg text-gray-900 dark:text-white">
                      {{ selectedEvento?.acao_correcao || '-' }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Aba Histórico -->
              <div v-if="detalhesTabAtiva === 'historico'" class="space-y-4">
                <div v-if="carregandoHistorico" class="text-center py-8">
                  <svg class="animate-spin h-8 w-8 text-blue-600 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none"
                    viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                    </path>
                  </svg>
                  <p class="mt-4 text-gray-600 dark:text-gray-400">Carregando histórico...</p>
                </div>

                <div v-else-if="historicoEvento.length === 0" class="text-center py-8 text-gray-500 dark:text-gray-400">
                  Nenhum histórico disponível para este evento.
                </div>

                <div v-else class="space-y-4">
                  <div v-for="item in historicoEvento" :key="item.id"
                    class="border-l-4 border-blue-500 pl-4 py-3 bg-gray-50 dark:bg-gray-700 rounded-r-lg">
                    <div class="flex justify-between items-start mb-2">
                      <div>
                        <h4 class="font-semibold text-gray-800 dark:text-white">{{ item.acao }}</h4>
                        <p class="text-sm text-gray-600 dark:text-gray-400" v-if="item.fase">
                          Fase: {{ item.fase }}
                        </p>
                      </div>
                      <span class="text-xs text-gray-500 dark:text-gray-400">
                        {{ formatDateTime(item.timestamp) }}
                      </span>
                    </div>

                    <div class="mt-2 space-y-1">
                      <p class="text-sm text-gray-700 dark:text-gray-300">
                        <span class="font-medium">Usuário:</span> {{ item.usuario }}
                      </p>

                      <p v-if="item.campo_alterado" class="text-sm text-gray-700 dark:text-gray-300">
                        <span class="font-medium">Campo:</span> {{ item.campo_alterado }}
                      </p>

                      <div v-if="item.valor_anterior || item.valor_novo"
                        class="mt-2 p-2 bg-white dark:bg-gray-800 rounded text-sm">
                        <p v-if="item.valor_anterior" class="text-red-600 dark:text-red-400">
                          <span class="font-medium">Antes:</span> {{ item.valor_anterior }}
                        </p>
                        <p v-if="item.valor_novo" class="text-green-600 dark:text-green-400 mt-1">
                          <span class="font-medium">Depois:</span> {{ item.valor_novo }}
                        </p>
                      </div>

                      <p v-if="item.detalhes" class="text-sm text-gray-600 dark:text-gray-400 mt-2">
                        {{ item.detalhes }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Footer do Modal de Detalhes -->
            <div
              class="sticky bottom-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 p-4 sm:p-6 rounded-b-2xl">
              <button @click="closeDetailsModal"
                class="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all font-medium">
                Fechar
              </button>
            </div>
          </div>
        </div>

        <div v-if="showPreviewAnexoModal"
          class="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm px-4">
          <div class="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] flex flex-col">
            <div
              class="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-gray-200 dark:border-gray-700">
              <div class="flex flex-col">
                <h3 class="text-sm sm:text-base font-semibold text-gray-800 dark:text-gray-100">
                  Visualizar anexo
                </h3>
                <p class="text-xs sm:text-sm text-gray-500 dark:text-gray-400 truncate">
                  {{ previewAnexoNome }}
                </p>
              </div>
              <button @click="fecharPreviewAnexo"
                class="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-300 transition">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div class="flex-1 overflow-hidden bg-gray-100 dark:bg-gray-800 flex items-center justify-center p-4">
              <template v-if="previewAnexoType === 'pdf'">
                <iframe :src="previewAnexoUrl"
                  class="w-full h-full min-h-[600px] rounded-lg border border-gray-200 dark:border-gray-700 bg-white"></iframe>
              </template>
              <template v-else-if="previewAnexoType === 'image'">
                <img :src="previewAnexoUrl" alt="Pré-visualização do anexo"
                  class="max-h-full max-w-full rounded-lg shadow-lg object-contain" />
              </template>
              <template v-else>
                <p class="text-sm text-gray-600 dark:text-gray-300">
                  Não foi possível visualizar este tipo de arquivo.
                </p>
              </template>
            </div>
            <div
              class="px-4 sm:px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end bg-white dark:bg-gray-900 rounded-b-2xl">
              <button @click="fecharPreviewAnexo"
                class="px-5 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition">
                Fechar
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>
  </BaseTemplate>
</template>

<script setup>
import { reactive, ref, onMounted, onUnmounted, computed, watch, nextTick } from 'vue'
import BaseTemplate from '../templates/BaseTemplate.vue'
import apiClient, { getUser } from '@/services/authService.js'
import { Chart, registerables } from 'chart.js'
import jsPDF from 'jspdf'
import { useUserStore } from '@/stores/userStore.js'

Chart.register(...registerables)
const userStore = useUserStore()

// Estados reativos
const eventos = ref([])
const searchTerm = ref('')
const selectedStatus = ref('todos')
const itemsPerPage = ref('10')
const currentPage = ref(1)
const totalItems = ref(0)
const totalPages = ref(0)
const showModal = ref(false)
const showDetailsModal = ref(false)
const selectedEvento = ref(null)
const currentPhase = ref(2)
const isSaving = ref(false)
// Tema alinhado ao BaseTemplate (usa a mesma chave 'darkMode')
const isDark = ref(typeof localStorage !== 'undefined' && localStorage.getItem('darkMode') === 'true')
const permissoesUsuario = ref(null)
const usuariosPermissoes = ref([])
const detalhesTabAtiva = ref('detalhes')
const historicoEvento = ref([])
const carregandoHistorico = ref(false)
const showBanner = ref(false)
const bannerMessage = ref('')
const bannerType = ref('success') // 'success' ou 'error'
const showGraficosRelatorios = ref(false)
const graficosRelatoriosTab = ref('graficos')
const carregandoGraficos = ref(false)
const carregandoRelatorio = ref(false)
const dadosGraficos = ref({})
const dadosRelatorio = ref({ eventos: [] })
const anexosEvento = ref([])
const arquivosUpload = ref({})
const isUploadingAnexo = ref(false)
const showAllAnexos = ref(false)
const showDemandaJudicial = ref(false)
const showPreviewAnexoModal = ref(false)
const previewAnexoUrl = ref('')
const previewAnexoType = ref('')
const previewAnexoNome = ref('')
// Função para calcular datas padrão (15 dias atrás até hoje)
const calcularDatasPadrao = () => {
  const hoje = new Date()
  const dataFim = hoje.toISOString().split('T')[0] // Formato YYYY-MM-DD

  const dataInicio = new Date(hoje)
  dataInicio.setDate(dataInicio.getDate() - 15)
  const dataInicioFormatada = dataInicio.toISOString().split('T')[0] // Formato YYYY-MM-DD

  return { dataInicio: dataInicioFormatada, dataFim }
}

const filtrosRelatorio = reactive({
  status: '',
  data_inicio: '',
  data_fim: '',
  local_evento: '',
  grau_dano: '',
  resolvido_prazo: null,
  tipo_evento: ''
})

// Inicializar datas padrão
const inicializarDatasPadrao = () => {
  const { dataInicio, dataFim } = calcularDatasPadrao()
  filtrosRelatorio.data_inicio = dataInicio
  filtrosRelatorio.data_fim = dataFim
}
const chartStatus = ref(null)
const chartMeses = ref(null)
const chartGrau = ref(null)
let chartStatusInstance = null
let chartMesesInstance = null
let chartGrauInstance = null

// Estados de controle de fases
const fase2Completa = ref(false)
const fase21Completa = ref(false)
const fase3Completa = ref(false)
const isConcluido = ref(false)
const fasesSalvas = ref({
  fase2: false,
  fase21: false,
  fase3: false,
  fase4: false
})

// Fases do modal
const fases = [
  { id: 1, name: 'Fase 1 - Informações' },
  { id: 2, name: 'Fase 2 - Análise' },
  { id: 21, name: 'Fase 2.1 - Análise Detalhada' },
  { id: 3, name: 'Fase 3 - Follow-up' },
  { id: 4, name: 'Fase 4 - Avaliação' }
]

// Formulário do modal
const modalForm = reactive({
  // Fase 2
  dataInicioAnalise: '',
  dataDevolucaoAnalise: '',
  responsavelAnalise: '',
  identificacaoCausa: '',
  classificacaoDano: '',
  grauDano: '',
  acaoCorretiva: '',
  responsavelAcao: '',
  prazoAcao: '',
  evidenciaConclusao: '',

  // Fase 3
  dataFollowUp: '',
  dataDevolucaoFollowUp: '',
  analiseFollowUp: '',
  responsavelFollowUp: '',

  // Fase 4
  avaliacaoEficacia: '',
  responsavelAvaliacao: '',
  prazoAvaliacao: '',
  statusFinal: ''
})

const camposBloqueadosFase21 = reactive({
  identificacaoCausa: false,
  classificacaoDano: false,
  grauDano: false,
  acaoCorretiva: false,
  prazoAcao: false,
  evidenciaConclusao: false
})

// Funções de paginação




// Eventos filtrados (agora apenas retorna os eventos carregados, pois a filtragem é feita no backend)
const filteredEventos = computed(() => eventos.value)

const canListarEventos = computed(() => !!permissoesUsuario.value?.pode_listar_eventos)
const canVerEvento = computed(() => !!permissoesUsuario.value?.pode_ver_evento)
const canGerenciarEventos = computed(() => !!permissoesUsuario.value?.pode_gerenciar_eventos)
const canVerHistorico = computed(() => !!permissoesUsuario.value?.pode_ver_historico)
const canVerGraficos = computed(() => !!permissoesUsuario.value?.pode_ver_graficos)
const canVerRelatorios = computed(() => !!permissoesUsuario.value?.pode_ver_relatorios)
const canVerGraficosRelatorios = computed(() => canVerGraficos.value || canVerRelatorios.value)
const canAnexar = computed(() => !!permissoesUsuario.value?.pode_anexar)
const canVerAnexos = computed(() => !!permissoesUsuario.value?.pode_ver_anexos)

const usuariosFase2 = computed(() => usuariosPermissoes.value)
const usuariosFase3 = computed(() => usuariosPermissoes.value)
const usuariosFase4 = computed(() => usuariosPermissoes.value)

const temPermissaoFase = (faseNumero) => {
  if (!permissoesUsuario.value) return false
  if (faseNumero === 1) return true
  if (faseNumero === 2) return !!permissoesUsuario.value.fase_2
  if (faseNumero === 21) return !!permissoesUsuario.value.fase_2_1
  if (faseNumero === 3) return !!permissoesUsuario.value.fase_3
  if (faseNumero === 4) return !!permissoesUsuario.value.fase_4
  return false
}

const toggleGraficosRelatorios = () => {
  if (!canVerGraficosRelatorios.value) return
  showGraficosRelatorios.value = !showGraficosRelatorios.value
  if (showGraficosRelatorios.value) {
    if (!canVerGraficos.value && canVerRelatorios.value) {
      graficosRelatoriosTab.value = 'relatorios'
    }
    if (!canVerRelatorios.value && canVerGraficos.value) {
      graficosRelatoriosTab.value = 'graficos'
    }
  }
}

const isFaseReadOnly = (faseNumero) => {
  return !temPermissaoFase(faseNumero)
}

const isFaseDisponivel = (faseNumero) => {
  return temPermissaoFase(faseNumero)
}

const faseKeyToNumero = (faseKey) => {
  if (faseKey === '2.1') return 21
  const numero = Number(faseKey)
  return Number.isNaN(numero) ? null : numero
}

const getAnexosByFase = (faseKey) => {
  return anexosEvento.value.filter(anexo => anexo.fase === faseKey)
}

const anexosDemandaJudicial = computed(() =>
  anexosEvento.value.filter(anexo => anexo.fase === 'demanda_judicial')
)

const onArquivoAnexoChange = (event, faseKey) => {
  const files = event.target.files ? Array.from(event.target.files) : []
  if (!files.length) return
  arquivosUpload.value = {
    ...arquivosUpload.value,
    [faseKey]: files
  }
}

const loadAnexosEvento = async (eventoId) => {
  try {
    const response = await apiClient.get(`/api/v1/eventos-adversos/${eventoId}/anexos/`)
    anexosEvento.value = Array.isArray(response.data) ? response.data : []
  } catch (error) {
    console.error('Erro ao carregar anexos do evento:', error)
    anexosEvento.value = []
  }
}

const uploadAnexoFase = async (faseKey) => {
  const faseNumero = faseKeyToNumero(faseKey)
  if (!selectedEvento.value || !faseNumero) return
  if (!canAnexar.value) return

  const files = arquivosUpload.value[faseKey]
  if (!files || !files.length || isUploadingAnexo.value) return

  isUploadingAnexo.value = true
  try {
    for (const file of files) {
      const formData = new FormData()
      formData.append('arquivo', file)
      formData.append('fase', faseKey)
      formData.append('nome', file.name)

      const response = await apiClient.post(
        `/api/v1/eventos-adversos/${selectedEvento.value.id}/anexo/`,
        formData
      )

      if (response && response.data) {
        anexosEvento.value = [response.data, ...anexosEvento.value]
      }
    }

    arquivosUpload.value = {
      ...arquivosUpload.value,
      [faseKey]: null
    }
  } catch (error) {
    console.error('Erro ao enviar anexo da fase:', error)
  } finally {
    isUploadingAnexo.value = false
  }
}

const uploadDemandaJudicial = async () => {
  const faseKey = 'demanda_judicial'
  if (!selectedEvento.value) return
  if (!canAnexar.value) return
  const files = arquivosUpload.value[faseKey]
  if (!files || !files.length || isUploadingAnexo.value) return

  isUploadingAnexo.value = true
  try {
    for (const file of files) {
      const formData = new FormData()
      formData.append('arquivo', file)
      formData.append('fase', faseKey)
      formData.append('nome', file.name)

      const response = await apiClient.post(
        `/api/v1/eventos-adversos/${selectedEvento.value.id}/anexo/`,
        formData
      )

      if (response && response.data) {
        anexosEvento.value = [response.data, ...anexosEvento.value]
      }
    }

    arquivosUpload.value = {
      ...arquivosUpload.value,
      [faseKey]: null
    }
  } catch (error) {
    console.error('Erro ao enviar anexo de demanda judicial:', error)
  } finally {
    isUploadingAnexo.value = false
  }
}

const abrirAnexo = (anexo) => {
  if (!anexo || !anexo.url || !anexo.nome) return

  const nome = anexo.nome.toLowerCase()
  const extensao = nome.split('.').pop()

  if (extensao === 'pdf') {
    previewAnexoType.value = 'pdf'
    previewAnexoUrl.value = anexo.url
    previewAnexoNome.value = anexo.nome
    showPreviewAnexoModal.value = true
    return
  }

  const imagens = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp']
  if (extensao && imagens.includes(extensao)) {
    previewAnexoType.value = 'image'
    previewAnexoUrl.value = anexo.url
    previewAnexoNome.value = anexo.nome
    showPreviewAnexoModal.value = true
    return
  }

  window.open(anexo.url, '_blank')
}

const fecharPreviewAnexo = () => {
  showPreviewAnexoModal.value = false
  previewAnexoUrl.value = ''
  previewAnexoType.value = ''
  previewAnexoNome.value = ''
}

// Funções de paginação
const previousPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--
    loadEventos()
  }
}

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++
    loadEventos()
  }
}

const tiposEventoOptions = computed(() => {
  const tiposSet = new Set()
  eventos.value.forEach(evento => {
    const tipos = evento.tipos_evento
    if (Array.isArray(tipos)) {
      tipos.forEach(t => {
        if (t) tiposSet.add(t)
      })
    } else if (tipos) {
      tiposSet.add(tipos)
    }
  })
  return Array.from(tiposSet).sort((a, b) => a.localeCompare(b, 'pt-BR'))
})

const locaisEventoOptions = computed(() => {
  const locaisSet = new Set()
  eventos.value.forEach(evento => {
    const local = evento.local_evento
    if (local) {
      locaisSet.add(local)
    }
  })
  return Array.from(locaisSet).sort((a, b) => a.localeCompare(b, 'pt-BR'))
})

const isGrauDanoDisabled = computed(() => {
  return (
    isFaseReadOnly(21) ||
    camposBloqueadosFase21.grauDano ||
    modalForm.classificacaoDano !== 'Sim'
  )
})

const setClassificacaoDano = (valor) => {
  if (isFaseReadOnly(21) || camposBloqueadosFase21.classificacaoDano) return
  modalForm.classificacaoDano = valor
  if (valor !== 'Sim') {
    modalForm.grauDano = ''
  }
}

const loadEventos = async () => {
  if (!canListarEventos.value) return
  try {
    const params = {
      page: currentPage.value,
      per_page: parseInt(itemsPerPage.value),
      search: searchTerm.value || undefined,
      status: selectedStatus.value !== 'todos' ? selectedStatus.value : undefined
    }

    const response = await apiClient.get('/api/v1/eventos-adversos/gerenciar/', { params })
    const data = response.data
    eventos.value = data.eventos.map(evento => ({
      id: evento.id,
      protocolo: evento.protocolo,
      paciente: evento.paciente || evento.nome_usuario,
      data_ocorrencia: evento.data_ocorrencia || evento.data_evento,
      data_registro: evento.data_registro,
      status: evento.status || 'Pendente',
      responsavel: evento.responsavel,
      cpf_usuario: evento.cpf_usuario,
      data_nascimento: evento.data_nascimento,
      tipos_evento: evento.tipos_evento || evento.tipo_evento,
      local_evento: evento.local_evento || evento.local,
      condicoes_usuario: evento.condicoes_usuario,
      resumo_evento: evento.resumo_evento || evento.descricao,
      acao_correcao: evento.acao_correcao
    }))
    totalItems.value = Number(data.total) || eventos.value.length || 0
    totalPages.value = Number(data.pages) || Math.ceil(totalItems.value / parseInt(itemsPerPage.value)) || 1
  } catch (error) {
    console.error('Erro ao carregar eventos:', error)
    eventos.value = []
  }
}

const loadPermissoesUsuario = async () => {
  try {
    if (!userStore.eventosAdversosPermissions) {
      await getUser()
    }
    permissoesUsuario.value = userStore.eventosAdversosPermissions || null
  } catch (error) {
    console.error('Erro ao carregar permissões do usuário:', error)
    permissoesUsuario.value = null
  }
}

const loadUsuariosPermissoes = async () => {
  if (!canGerenciarEventos.value) {
    usuariosPermissoes.value = []
    return
  }
  try {
    const response = await apiClient.get('/api/v1/eventos-adversos/permissoes/usuarios/')
    usuariosPermissoes.value = Array.isArray(response.data) ? response.data : []
  } catch (error) {
    console.error('Erro ao carregar usuários com permissão:', error)
    usuariosPermissoes.value = []
  }
}

const formatDate = (dateString) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleDateString('pt-BR')
}

const getStatusClass = (status) => {
  const classes = {
    'Pendente': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
    'Em Análise': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
    'FASE 3': 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300',
    'Em Follow-up': 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
    'FASE 4': 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-300',
    'Em Avaliação': 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-300',
    'Concluído': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
    'Arquivado': 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300'
  }
  return classes[status] || classes['Pendente']
}

const verificarFaseCompleta = (faseNumero) => {
  switch (faseNumero) {
    case 2:
      return !!(
        modalForm.dataInicioAnalise &&
        modalForm.responsavelAnalise &&
        modalForm.responsavelAcao
      )
    case 21:
      return !!(
        modalForm.identificacaoCausa &&
        modalForm.classificacaoDano &&
        modalForm.acaoCorretiva &&
        modalForm.prazoAcao
      )
    case 3:
      return !!(
        modalForm.dataFollowUp &&
        modalForm.dataDevolucaoFollowUp &&
        modalForm.analiseFollowUp &&
        modalForm.responsavelFollowUp
      )
    case 4:
      return !!(
        modalForm.avaliacaoEficacia &&
        modalForm.responsavelAvaliacao &&
        modalForm.prazoAvaliacao
      )
    default:
      return false
  }
}

const openModal = async (evento) => {
  selectedEvento.value = evento
  currentPhase.value = 1
  showAllAnexos.value = false
  showDemandaJudicial.value = false

  camposBloqueadosFase21.identificacaoCausa = false
  camposBloqueadosFase21.classificacaoDano = false
  camposBloqueadosFase21.grauDano = false
  camposBloqueadosFase21.acaoCorretiva = false
  camposBloqueadosFase21.prazoAcao = false
  camposBloqueadosFase21.evidenciaConclusao = false

  try {
    const response = await apiClient.get(`/api/v1/eventos-adversos/${evento.id}/gerenciar/`)
    const data = response.data

    // Preencher formulário com dados existentes
    modalForm.dataInicioAnalise = data.data_inicio_analise || ''
    modalForm.dataDevolucaoAnalise = data.data_devolucao_analise || ''
    modalForm.responsavelAnalise = data.responsavel_analise || ''
    modalForm.identificacaoCausa = data.identificacao_causa || ''
    modalForm.classificacaoDano = data.classificacao_dano || ''
    modalForm.grauDano = data.grau_dano || ''
    modalForm.acaoCorretiva = data.acao_corretiva || ''
    modalForm.responsavelAcao = data.responsavel_acao || ''
    modalForm.prazoAcao = data.prazo_acao || ''
    modalForm.evidenciaConclusao = data.evidencia_conclusao || ''
    modalForm.dataFollowUp = data.data_follow_up || ''
    modalForm.dataDevolucaoFollowUp = data.data_devolucao_follow_up || ''
    modalForm.analiseFollowUp = data.analise_follow_up || ''
    modalForm.responsavelFollowUp = data.responsavel_follow_up || ''
    modalForm.avaliacaoEficacia = data.avaliacao_eficacia || ''
    modalForm.responsavelAvaliacao = data.responsavel_avaliacao || ''
    modalForm.prazoAvaliacao = data.prazo_avaliacao || ''
    modalForm.statusFinal = data.status || 'Pendente'

    camposBloqueadosFase21.identificacaoCausa = !!data.identificacao_causa
    camposBloqueadosFase21.classificacaoDano = !!data.classificacao_dano
    camposBloqueadosFase21.grauDano = !!data.grau_dano
    camposBloqueadosFase21.acaoCorretiva = !!data.acao_corretiva
    camposBloqueadosFase21.prazoAcao = !!data.prazo_acao
    camposBloqueadosFase21.evidenciaConclusao = !!data.evidencia_conclusao

    if (canVerAnexos.value) {
      await loadAnexosEvento(evento.id)
    } else {
      anexosEvento.value = []
    }

    isConcluido.value = data.status === 'Concluído'
    fase2Completa.value = verificarFaseCompleta(2)
    fase21Completa.value = verificarFaseCompleta(21)
    fase3Completa.value = verificarFaseCompleta(3)

    // Verificar quais fases já foram salvas (baseado na existência de dados)
    fasesSalvas.value.fase2 = !!(data.data_inicio_analise || data.responsavel_analise || data.responsavel_acao)
    fasesSalvas.value.fase21 = !!(data.identificacao_causa || data.classificacao_dano || data.acao_corretiva)
    fasesSalvas.value.fase3 = !!(data.data_follow_up || data.responsavel_follow_up || data.analise_follow_up)
    fasesSalvas.value.fase4 = !!(data.avaliacao_eficacia || data.responsavel_avaliacao || data.prazo_avaliacao)

    // Ajustar fase inicial baseado no progresso
    if (isConcluido.value) {
      currentPhase.value = 2 // Começar na fase 2 para visualização
    } else if (fase3Completa.value) {
      currentPhase.value = 4
    } else if (fase21Completa.value) {
      currentPhase.value = 3
    } else if (fase2Completa.value) {
      currentPhase.value = 21
    }

  } catch (error) {
    console.error('Erro ao carregar detalhes:', error)
    // Resetar formulário em caso de erro
    Object.keys(modalForm).forEach(key => {
      modalForm[key] = ''
    })
  }

  showModal.value = true
}

const closeModal = () => {
  showModal.value = false
  selectedEvento.value = null
  currentPhase.value = 2
  showAllAnexos.value = false
  showDemandaJudicial.value = false
  // Resetar estados de fases salvas ao fechar
  fasesSalvas.value = {
    fase2: false,
    fase21: false,
    fase3: false,
    fase4: false
  }

  camposBloqueadosFase21.identificacaoCausa = false
  camposBloqueadosFase21.classificacaoDano = false
  camposBloqueadosFase21.grauDano = false
  camposBloqueadosFase21.acaoCorretiva = false
  camposBloqueadosFase21.prazoAcao = false
  camposBloqueadosFase21.evidenciaConclusao = false
}

const openDetailsModal = (evento) => {
  selectedEvento.value = evento
  detalhesTabAtiva.value = 'detalhes'
  historicoEvento.value = []
  showDetailsModal.value = true
}

const closeDetailsModal = () => {
  showDetailsModal.value = false
  selectedEvento.value = null
  detalhesTabAtiva.value = 'detalhes'
  historicoEvento.value = []
}

const loadHistorico = async () => {
  if (!canVerHistorico.value) return
  if (!selectedEvento.value || historicoEvento.value.length > 0) return

  carregandoHistorico.value = true
  try {
    const response = await apiClient.get(`/api/v1/eventos-adversos/${selectedEvento.value.id}/historico/`)
    historicoEvento.value = response.data.historico || []
  } catch (error) {
    console.error('Erro ao carregar histórico:', error)
    historicoEvento.value = []
  } finally {
    carregandoHistorico.value = false
  }
}

const formatDateTime = (dateString) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const showBannerMessage = (message, type = 'success') => {
  bannerMessage.value = message
  bannerType.value = type
  showBanner.value = true

  // Auto-hide após 5 segundos
  setTimeout(() => {
    showBanner.value = false
  }, 5000)
}

// Funções de Gráficos e Relatórios
const carregarGraficos = async () => {
  if (!canVerGraficos.value) return
  carregandoGraficos.value = true
  try {
    const response = await apiClient.get('/api/v1/eventos-adversos/estatisticas/graficos/')
    dadosGraficos.value = response.data

    // Aguardar o próximo tick para garantir que os refs estejam disponíveis
    await nextTick()

    // Pequeno delay adicional para garantir que os canvas estejam renderizados
    setTimeout(() => {
      criarGraficos()
    }, 100)
  } catch (error) {
    console.error('Erro ao carregar gráficos:', error)
    showBannerMessage('Erro ao carregar gráficos', 'error')
  } finally {
    carregandoGraficos.value = false
  }
}

const criarGraficos = () => {
  // Destruir gráficos existentes
  if (chartStatusInstance) {
    chartStatusInstance.destroy()
    chartStatusInstance = null
  }
  if (chartMesesInstance) {
    chartMesesInstance.destroy()
    chartMesesInstance = null
  }
  if (chartGrauInstance) {
    chartGrauInstance.destroy()
    chartGrauInstance = null
  }

  const isDarkMode = isDark.value

  // Debug: verificar se os dados estão disponíveis
  console.log('Dados dos gráficos:', dadosGraficos.value)
  console.log('Refs dos canvas:', {
    chartStatus: chartStatus.value,
    chartMeses: chartMeses.value,
    chartGrau: chartGrau.value
  })

  // Gráfico por Status
  if (chartStatus.value && dadosGraficos.value.eventos_por_status && Array.isArray(dadosGraficos.value.eventos_por_status) && dadosGraficos.value.eventos_por_status.length > 0) {
    const ctx = chartStatus.value.getContext('2d')
    const statusData = dadosGraficos.value.eventos_por_status

    // Mapear cores baseado no status
    const getStatusColor = (status) => {
      const statusLower = status.toLowerCase()
      if (statusLower.includes('análise') || statusLower.includes('analise')) {
        return 'rgba(59, 130, 246, 0.8)' // Azul para "Em Análise"
      } else if (statusLower.includes('concluído') || statusLower.includes('concluido')) {
        return 'rgba(34, 197, 94, 0.8)' // Verde para "Concluído"
      } else if (statusLower.includes('pendente')) {
        return 'rgba(251, 191, 36, 0.8)' // Amarelo para "Pendente"
      } else if (statusLower.includes('follow-up') || statusLower.includes('followup')) {
        return 'rgba(139, 92, 246, 0.8)' // Roxo para "Follow-up"
      } else if (statusLower.includes('avaliação') || statusLower.includes('avaliacao')) {
        return 'rgba(236, 72, 153, 0.8)' // Rosa para "Avaliação"
      } else if (statusLower.includes('fase 3')) {
        return 'rgba(99, 102, 241, 0.8)' // Índigo para "FASE 3"
      } else if (statusLower.includes('fase 4')) {
        return 'rgba(168, 85, 247, 0.8)' // Roxo claro para "FASE 4"
      } else if (statusLower.includes('arquivado')) {
        return 'rgba(107, 114, 128, 0.8)' // Cinza para "Arquivado"
      }
      return 'rgba(156, 163, 175, 0.8)' // Cinza padrão
    }

    chartStatusInstance = new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels: statusData.map(s => s.status),
        datasets: [{
          data: statusData.map(s => s.total),
          backgroundColor: statusData.map(s => getStatusColor(s.status)),
          borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 1)',
          borderWidth: 2
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        plugins: {
          legend: {
            position: 'bottom',
            labels: {
              color: isDarkMode ? '#e5e7eb' : '#374151',
              padding: 15
            }
          }
        }
      }
    })
  }

  // Gráfico por Mês
  if (chartMeses.value && dadosGraficos.value.eventos_por_mes && dadosGraficos.value.eventos_por_mes.labels && dadosGraficos.value.eventos_por_mes.data) {
    const ctx = chartMeses.value.getContext('2d')
    const mesesData = dadosGraficos.value.eventos_por_mes

    chartMesesInstance = new Chart(ctx, {
      type: 'line',
      data: {
        labels: mesesData.labels,
        datasets: [{
          label: 'Eventos',
          data: mesesData.data,
          borderColor: 'rgba(59, 130, 246, 1)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          tension: 0.4,
          fill: true,
          borderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        plugins: {
          legend: {
            display: false
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              color: isDarkMode ? '#9ca3af' : '#6b7280'
            },
            grid: {
              color: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
            }
          },
          x: {
            ticks: {
              color: isDarkMode ? '#9ca3af' : '#6b7280'
            },
            grid: {
              color: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
            }
          }
        }
      }
    })
  }

  // Gráfico por Grau de Dano
  if (chartGrau.value && dadosGraficos.value.eventos_por_grau && dadosGraficos.value.eventos_por_grau.labels && dadosGraficos.value.eventos_por_grau.data) {
    const ctx = chartGrau.value.getContext('2d')
    const grauData = dadosGraficos.value.eventos_por_grau

    chartGrauInstance = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: grauData.labels,
        datasets: [{
          label: 'Quantidade',
          data: grauData.data,
          backgroundColor: [
            'rgba(34, 197, 94, 0.8)',
            'rgba(251, 146, 60, 0.8)',
            'rgba(239, 68, 68, 0.8)'
          ],
          borderColor: [
            'rgba(34, 197, 94, 1)',
            'rgba(251, 146, 60, 1)',
            'rgba(239, 68, 68, 1)'
          ],
          borderWidth: 2,
          borderRadius: 8
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        plugins: {
          legend: {
            display: false
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              color: isDarkMode ? '#9ca3af' : '#6b7280',
              stepSize: 1
            },
            grid: {
              color: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
            }
          },
          x: {
            ticks: {
              color: isDarkMode ? '#9ca3af' : '#6b7280'
            },
            grid: {
              display: false
            }
          }
        }
      }
    })
  }
}

const carregarRelatorio = async () => {
  if (!canVerRelatorios.value) return
  carregandoRelatorio.value = true
  try {
    const params = {}
    if (filtrosRelatorio.status) params.status = filtrosRelatorio.status
    if (filtrosRelatorio.data_inicio) params.data_inicio = filtrosRelatorio.data_inicio
    if (filtrosRelatorio.data_fim) params.data_fim = filtrosRelatorio.data_fim
    if (filtrosRelatorio.local_evento) params.local_evento = filtrosRelatorio.local_evento
    if (filtrosRelatorio.grau_dano) params.grau_dano = filtrosRelatorio.grau_dano
    if (filtrosRelatorio.resolvido_prazo !== null) params.resolvido_prazo = filtrosRelatorio.resolvido_prazo
    if (filtrosRelatorio.tipo_evento) params.tipo_evento = filtrosRelatorio.tipo_evento

    const response = await apiClient.get('/api/v1/eventos-adversos/relatorio/dados/', { params })
    dadosRelatorio.value = response.data
  } catch (error) {
    console.error('Erro ao carregar relatório:', error)
    showBannerMessage('Erro ao carregar relatório', 'error')
  } finally {
    carregandoRelatorio.value = false
  }
}

const gerarPDF = async () => {
  try {
    const nomeUsuario = userStore.nome || 'Usuário'

    const doc = new jsPDF('landscape', 'mm', 'a4')
    const pageWidth = doc.internal.pageSize.getWidth()
    const pageHeight = doc.internal.pageSize.getHeight()
    const margin = 15
    const tableWidth = pageWidth - (margin * 2)

    // Função para adicionar logo no rodapé
    const addFooterLogo = async () => {
      try {
        // Tentar carregar a logo usando import dinâmico
        let logoBase64 = null

        try {
          // Tentar importar a logo
          const logoModule = await import('@/assets/logo_ccdti.png')
          const logoUrl = logoModule.default || logoModule

          // Converter para base64 usando fetch
          const response = await fetch(logoUrl)
          const blob = await response.blob()
          logoBase64 = await new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.onloadend = () => resolve(reader.result)
            reader.onerror = reject
            reader.readAsDataURL(blob)
          })
        } catch (logoError) {
          console.log('Não foi possível carregar a logo:', logoError)
        }

        const logoHeight = 15
        const logoY = pageHeight - margin - logoHeight

        // Adicionar linha separadora
        doc.setDrawColor(200, 200, 200)
        doc.setLineWidth(0.5)
        doc.line(margin, logoY - 5, pageWidth - margin, logoY - 5)

        // Adicionar logo se disponível
        if (logoBase64) {
          try {
            const logoWidth = 35
            const logoHeightAdjusted = 10
            const logoX = pageWidth / 2 - logoWidth / 2
            doc.addImage(logoBase64, 'PNG', logoX, logoY, logoWidth, logoHeightAdjusted)

            // Texto abaixo da logo
            doc.setFontSize(8)
            doc.setTextColor(100, 100, 100)
            doc.text('Desenvolvido por TI - CCDTI', pageWidth / 2, logoY + logoHeightAdjusted + 5, { align: 'center' })
          } catch (imgError) {
            console.log('Erro ao adicionar imagem:', imgError)
            // Fallback para texto
            doc.setFontSize(9)
            doc.setTextColor(100, 100, 100)
            doc.text('Desenvolvido por TI - CCDTI', pageWidth / 2, logoY + 8, { align: 'center' })
          }
        } else {
          // Texto CCDTI no rodapé (fallback)
          doc.setFontSize(9)
          doc.setTextColor(100, 100, 100)
          doc.text('Desenvolvido por TI - CCDTI', pageWidth / 2, logoY + 8, { align: 'center' })
        }
      } catch (error) {
        console.log('Erro ao adicionar rodapé:', error)
        const logoHeight = 15
        const logoY = pageHeight - margin - logoHeight

        // Adicionar linha separadora
        doc.setDrawColor(200, 200, 200)
        doc.setLineWidth(0.5)
        doc.line(margin, logoY - 5, pageWidth - margin, logoY - 5)

        // Texto CCDTI no rodapé
        doc.setFontSize(9)
        doc.setTextColor(100, 100, 100)
        doc.text('Desenvolvido por TI - CCDTI', pageWidth / 2, logoY + 8, { align: 'center' })
      }
    }

    // Função para adicionar número de página
    const addPageNumber = (pageNum, totalPages) => {
      doc.setFontSize(8)
      doc.setTextColor(150, 150, 150)
      doc.text(`Página ${pageNum} de ${totalPages}`, pageWidth / 2, pageHeight - 5, { align: 'center' })
    }

    let currentY = margin + 10

    // Calcular quantas linhas cabem por página (considerando espaço para rodapé)
    const headerHeight = 8
    const rowHeight = 7
    const footerSpace = 30
    const availableHeight = pageHeight - currentY - footerSpace - headerHeight
    const rowsPerPage = Math.floor(availableHeight / rowHeight)
    const totalPages = Math.ceil(dadosRelatorio.value.eventos.length / rowsPerPage) || 1

    // Título centralizado
    doc.setFontSize(22)
    doc.setFont(undefined, 'bold')
    doc.setTextColor(30, 58, 138)
    const titleText = 'Relatório de Eventos Adversos'
    doc.text(titleText, pageWidth / 2, currentY, { align: 'center' })

    currentY += 10

    // Linha decorativa abaixo do título
    doc.setDrawColor(59, 130, 246)
    doc.setLineWidth(1)
    doc.line(pageWidth / 2 - 50, currentY, pageWidth / 2 + 50, currentY)

    currentY += 8

    // Informações do relatório em caixas estilizadas
    doc.setFontSize(10)
    doc.setFont(undefined, 'normal')
    doc.setTextColor(0, 0, 0)

    // Caixa de informações
    const infoBoxY = currentY
    doc.setFillColor(240, 245, 255)
    doc.roundedRect(margin, infoBoxY, tableWidth, 18, 3, 3, 'F')

    doc.setFontSize(9)
    doc.setTextColor(30, 58, 138)
    doc.setFont(undefined, 'bold')
    doc.text('Informações do Relatório', margin + 5, infoBoxY + 6)

    doc.setFontSize(8)
    doc.setTextColor(0, 0, 0)
    doc.setFont(undefined, 'normal')
    doc.text(`Data de geração: ${new Date().toLocaleString('pt-BR')}`, margin + 5, infoBoxY + 11)
    doc.text(`Total de eventos: ${dadosRelatorio.value.total}`, pageWidth / 2, infoBoxY + 11)
    doc.text(`Emitido por: ${nomeUsuario}`, margin + 5, infoBoxY + 16)

    currentY += 23

    // Filtros aplicados em caixa estilizada
    if (Object.values(dadosRelatorio.value.filtros_aplicados).some(v => v !== null && v !== '')) {
      const filtrosBoxY = currentY
      doc.setFillColor(249, 250, 251)
      doc.roundedRect(margin, filtrosBoxY, tableWidth, 25, 3, 3, 'F')

      doc.setFontSize(9)
      doc.setTextColor(30, 58, 138)
      doc.setFont(undefined, 'bold')
      doc.text('Filtros Aplicados:', margin + 5, filtrosBoxY + 6)

      doc.setFontSize(8)
      doc.setTextColor(0, 0, 0)
      doc.setFont(undefined, 'normal')

      let filtroY = filtrosBoxY + 11
      const filtros = dadosRelatorio.value.filtros_aplicados
      const filtrosText = []

      if (filtros.status) filtrosText.push(`Status: ${filtros.status}`)
      if (filtros.data_inicio) filtrosText.push(`Data Início: ${filtros.data_inicio}`)
      if (filtros.data_fim) filtrosText.push(`Data Fim: ${filtros.data_fim}`)
      if (filtros.local_evento) filtrosText.push(`Local: ${filtros.local_evento}`)
      if (filtros.grau_dano) {
        const grauMap = { '1': 'Leve', '2': 'Moderado', '3': 'Grave' }
        filtrosText.push(`Grau de Dano: ${grauMap[filtros.grau_dano] || filtros.grau_dano}`)
      }
      if (filtros.resolvido_prazo !== null) {
        filtrosText.push(`Resolvido no Prazo: ${filtros.resolvido_prazo ? 'Sim' : 'Não'}`)
      }

      // Organizar filtros em duas colunas
      const col1X = margin + 5
      const col2X = pageWidth / 2
      filtrosText.forEach((text, index) => {
        const xPos = index % 2 === 0 ? col1X : col2X
        const yPos = filtroY + Math.floor(index / 2) * 5
        doc.text(text, xPos, yPos)
      })

      currentY += 30
    }

    // Tabela de eventos
    const headers = ['Protocolo', 'Paciente', 'Data Evento', 'Status', 'Local', 'Grau Dano', 'Dias Res.', 'Prazo']
    const colWidths = [30, 50, 30, 30, 45, 25, 25, 20]
    const startX = margin
    let tableY = currentY
    let pageNum = 1

    // Cabeçalho da tabela estilizado
    doc.setFillColor(30, 58, 138)
    doc.roundedRect(startX, tableY, tableWidth, 8, 2, 2, 'F')
    doc.setTextColor(255, 255, 255)
    doc.setFontSize(9)
    doc.setFont(undefined, 'bold')

    let xPos = startX + 3
    headers.forEach((header, index) => {
      doc.text(header, xPos, tableY + 5.5)
      xPos += colWidths[index]
    })

    tableY += 8
    doc.setTextColor(0, 0, 0)
    doc.setFont(undefined, 'normal')

    // Linhas da tabela
    for (let index = 0; index < dadosRelatorio.value.eventos.length; index++) {
      const evento = dadosRelatorio.value.eventos[index]

      // Verificar se precisa de nova página
      if (tableY > pageHeight - 40) {
        // Adicionar rodapé na página atual
        await addFooterLogo()
        addPageNumber(pageNum, totalPages)

        // Nova página
        doc.addPage()
        pageNum++
        tableY = margin + 10

        // Recriar cabeçalho da tabela
        doc.setFillColor(30, 58, 138)
        doc.roundedRect(startX, tableY, tableWidth, 8, 2, 2, 'F')
        doc.setTextColor(255, 255, 255)
        doc.setFontSize(9)
        doc.setFont(undefined, 'bold')

        xPos = startX + 3
        headers.forEach((header, headerIndex) => {
          doc.text(header, xPos, tableY + 5.5)
          xPos += colWidths[headerIndex]
        })

        tableY += 8
        doc.setTextColor(0, 0, 0)
        doc.setFont(undefined, 'normal')
      }

      // Linha alternada com cor de fundo
      if (index % 2 === 0) {
        doc.setFillColor(249, 250, 251)
        doc.roundedRect(startX, tableY, tableWidth, 7, 1, 1, 'F')
      }

      // Borda da linha
      doc.setDrawColor(229, 231, 235)
      doc.setLineWidth(0.2)
      doc.line(startX, tableY + 7, startX + tableWidth, tableY + 7)

      xPos = startX + 3
      doc.setFontSize(8)

      const rowData = [
        evento.protocolo || '-',
        (evento.paciente || '-').substring(0, 25),
        formatDate(evento.data_evento),
        evento.status || '-',
        (evento.local_evento || '-').substring(0, 25),
        evento.grau_dano ? (evento.grau_dano === '1' ? 'Leve' : evento.grau_dano === '2' ? 'Moderado' : 'Grave') : '-',
        evento.dias_resolucao !== null ? `${evento.dias_resolucao}d` : '-',
        evento.dentro_prazo !== null ? (evento.dentro_prazo ? 'Sim' : 'Não') : '-'
      ]

      rowData.forEach((cell, cellIndex) => {
        // Truncar texto se necessário
        const maxWidth = colWidths[cellIndex] - 2
        let text = String(cell)
        if (doc.getTextWidth(text) > maxWidth) {
          while (doc.getTextWidth(text + '...') > maxWidth && text.length > 0) {
            text = text.slice(0, -1)
          }
          text = text + '...'
        }

        doc.text(text, xPos, tableY + 5)
        xPos += colWidths[cellIndex]
      })

      tableY += 7
    }

    // Adicionar rodapé na última página
    await addFooterLogo()
    addPageNumber(pageNum, totalPages)

    // Abrir PDF em nova guia ao invés de fazer download
    const pdfBlob = doc.output('blob')
    const pdfUrl = URL.createObjectURL(pdfBlob)
    window.open(pdfUrl, '_blank')

    // Limpar a URL do objeto após um tempo para liberar memória
    setTimeout(() => {
      URL.revokeObjectURL(pdfUrl)
    }, 100)

    showBannerMessage('PDF gerado com sucesso!', 'success')
  } catch (error) {
    console.error('Erro ao gerar PDF:', error)
    showBannerMessage('Erro ao gerar PDF', 'error')
  }
}

// Watch para carregar gráficos quando a aba for aberta
watch([showGraficosRelatorios, graficosRelatoriosTab], ([show, tab]) => {
  if (show && tab === 'graficos') {
    // Limpar dados anteriores para forçar recarregamento
    dadosGraficos.value = {}
    carregarGraficos()
  } else if (show && tab === 'relatorios') {
    // Inicializar datas padrão quando a aba de relatórios for aberta
    if (!filtrosRelatorio.data_inicio || !filtrosRelatorio.data_fim) {
      inicializarDatasPadrao()
    }
  }
})

// Watch para recriar gráficos quando o tema mudar
watch(isDark, () => {
  if (showGraficosRelatorios.value && graficosRelatoriosTab.value === 'graficos' && Object.keys(dadosGraficos.value).length > 0) {
    setTimeout(() => {
      criarGraficos()
    }, 100)
  }
})

const saveChanges = async () => {
  if (isSaving.value) return

  isSaving.value = true

  try {


    // Atualizar estados das fases baseado no que está sendo salvo
    let statusFinal = 'Pendente'

    switch (currentPhase.value) {
      case 2:
        if (verificarFaseCompleta(2)) {
          fase2Completa.value = true
          statusFinal = 'Em Análise'
        } else {
          statusFinal = 'Em Análise'
        }
        break

      case 21:
        if (verificarFaseCompleta(21)) {
          fase21Completa.value = true
          statusFinal = 'FASE 3'
        } else {
          statusFinal = 'Em Análise'
        }
        break

      case 3:
        if (verificarFaseCompleta(3)) {
          fase3Completa.value = true
          statusFinal = 'FASE 4'
        } else {
          statusFinal = 'Em Follow-up'
        }
        break

      case 4:
        if (verificarFaseCompleta(4)) {
          statusFinal = modalForm.statusFinal || 'Concluído'
          isConcluido.value = true
        } else {
          statusFinal = 'Em Avaliação'
        }
        break

      default:
        statusFinal = modalForm.statusFinal || 'Pendente'
    }

    const payload = {
      data_inicio_analise: modalForm.dataInicioAnalise,
      data_devolucao_analise: modalForm.dataDevolucaoAnalise,
      responsavel_analise: modalForm.responsavelAnalise,
      identificacao_causa: modalForm.identificacaoCausa,
      classificacao_dano: modalForm.classificacaoDano,
      grau_dano: modalForm.grauDano,
      acao_corretiva: modalForm.acaoCorretiva,
      responsavel_acao: modalForm.responsavelAcao,
      prazo_acao: modalForm.prazoAcao,
      evidencia_conclusao: modalForm.evidenciaConclusao,
      data_follow_up: modalForm.dataFollowUp,
      data_devolucao_follow_up: modalForm.dataDevolucaoFollowUp,
      analise_follow_up: modalForm.analiseFollowUp,
      responsavel_follow_up: modalForm.responsavelFollowUp,
      avaliacao_eficacia: modalForm.avaliacaoEficacia,
      responsavel_avaliacao: modalForm.responsavelAvaliacao,
      prazo_avaliacao: modalForm.prazoAvaliacao,
      status: statusFinal
    }

    const response = await apiClient.put(`/api/v1/eventos-adversos/${selectedEvento.value.id}/gerenciar/`, payload)
    const updatedEvento = response.data
    const eventoIndex = eventos.value.findIndex(e => e.id === selectedEvento.value.id)
    if (eventoIndex !== -1) {
      eventos.value[eventoIndex].status = updatedEvento.status || 'Pendente'
    }

    if (currentPhase.value === 21) {
      camposBloqueadosFase21.identificacaoCausa = !!modalForm.identificacaoCausa
      camposBloqueadosFase21.classificacaoDano = !!modalForm.classificacaoDano
      camposBloqueadosFase21.grauDano = !!modalForm.grauDano
      camposBloqueadosFase21.acaoCorretiva = !!modalForm.acaoCorretiva
      camposBloqueadosFase21.prazoAcao = !!modalForm.prazoAcao
      camposBloqueadosFase21.evidenciaConclusao = !!modalForm.evidenciaConclusao
    }

    // Marcar a fase atual como salva
    switch (currentPhase.value) {
      case 2:
        fasesSalvas.value.fase2 = true
        break
      case 21:
        fasesSalvas.value.fase21 = true
        break
      case 3:
        fasesSalvas.value.fase3 = true
        break
      case 4:
        fasesSalvas.value.fase4 = true
        break
    }

    // Notifications are now handled automatically by the backend when responsible users are changed

    // Atualizar status na lista sem fechar o modal
    await loadEventos()

    // Mostrar mensagem de sucesso
    showBannerMessage('Alterações salvas com sucesso!', 'success')
  } catch (error) {
    console.error('Erro ao salvar:', error)
    showBannerMessage('Erro ao salvar alterações. Tente novamente.', 'error')
  } finally {
    isSaving.value = false
  }
}

// Observer do tema (controlado pelo BaseTemplate) para atualizar gráficos
let themeObserver = null

onMounted(() => {
  // Tema é controlado pelo BaseTemplate; sincronizamos isDark para os gráficos
  isDark.value = document.documentElement.classList.contains('dark')
  themeObserver = new MutationObserver(() => {
    isDark.value = document.documentElement.classList.contains('dark')
  })
  themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })

  // Inicializar datas padrão para o relatório
  inicializarDatasPadrao()

  loadPermissoesUsuario().then(async () => {
    if (canListarEventos.value) {
      await loadEventos()
    }
    await loadUsuariosPermissoes()
  })

  // Verificar se há parâmetro de evento na URL para abrir modal automaticamente
  const urlParams = new URLSearchParams(window.location.search)
  const eventoId = urlParams.get('evento')
  if (eventoId) {
    // Aguardar carregamento dos eventos antes de abrir o modal
    setTimeout(() => {
      const evento = eventos.value.find(e => e.id === parseInt(eventoId))
      if (evento) {
        if (canGerenciarEventos.value) {
          openModal(evento)
        } else if (canVerEvento.value) {
          openDetailsModal(evento)
        }
      }
    }, 500)
  }
})

onUnmounted(() => {
  if (themeObserver) {
    themeObserver.disconnect()
  }
})

// Watchers para atualização automática
watch([searchTerm, selectedStatus, itemsPerPage], () => {
  currentPage.value = 1 // Resetar para primeira página ao filtrar
  if (canListarEventos.value) {
    loadEventos()
  }
})
</script>
